import React, { useMemo, useState, useEffect, useRef, Component } from 'react';
import { SandpackProvider, SandpackLayout, SandpackPreview, useSandpackNavigation, useSandpack, BackwardIcon, ForwardIcon, RefreshIcon } from '@codesandbox/sandpack-react';
import { sanitizeAllFiles } from '../services/codeSanitizer';
import { validateAllFiles } from '../services/babelValidator';
import { API_BASE, authFetch } from '../services/api';
import { useTheme } from '../hooks/useTheme';
import desktopIcon from '../computer.png';
import mobileIcon from '../iphone.png';
import tabletIcon from '../tablet.png';
import { PreviewSkeleton } from './Skeleton';

/** True when file uses react-router DOM components (not names like RouteSection). */
function usesReactRouter(code) {
  if (!code) return false;
  if (code.includes('useNavigate')) return true;
  if (/\b<Link[\s/>]/.test(code)) return true;
  if (/\b<Routes[\s/>]/.test(code)) return true;
  if (/\b<Route[\s/>]/.test(code)) return true;
  return false;
}

class PreviewErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, useLastWorking: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error) {
    console.warn('[LivePreview] Caught error:', error?.message);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 24, textAlign: 'center', color: '#f59e0b', background: 'var(--bg-card)', borderRadius: 12, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: 48, marginBottom: 12 }}>⚠️</span>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>Preview encountered a syntax error</p>
          <p style={{ fontSize: 12, color: '#64748b', maxWidth: 400 }}>
            {this.state.error?.message?.slice(0, 200) || 'Babel transpiler failed.'}
          </p>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>The code has been auto-fixed where possible. Remaining errors are shown above.</p>
          <button onClick={() => this.setState({ hasError: false, error: null })}
            style={{ marginTop: 16, padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
            ↻ Retry Preview
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
const detectFramework = (files) => {
  if (!files || Object.keys(files).length === 0) return 'unknown';

  const paths = Object.keys(files);
  const allContent = Object.values(files).join('\n');

  if (paths.some(p => p.match(/\.(jsx|tsx)$/))) return 'react';
  if (allContent.includes('from "react"') || allContent.includes("from 'react'")) return 'react';
  if (paths.some(p => p.includes('App.jsx') || p.includes('App.tsx'))) return 'react';

  // Angular — check BEFORE vue since both use .ts
  if (allContent.includes('@angular/core') || allContent.includes('@angular/common')) return 'angular';
  if (paths.some(p => p.includes('.component.ts') || p.includes('.module.ts'))) return 'angular';
  if (paths.some(p => p.includes('angular.json'))) return 'angular';

  if (paths.some(p => p.endsWith('.vue'))) return 'vue';
  if (allContent.includes('createApp') || allContent.includes('defineComponent')) return 'vue';

  if (paths.some(p => p.endsWith('index.html'))) return 'html';
  if (allContent.includes('<!DOCTYPE html') || allContent.includes('<html')) return 'html';

  if (allContent.includes("require('express')") || allContent.includes('from "express"')) return 'node';

  if (paths.some(p => p.endsWith('.py'))) return 'python';

  if (paths.some(p => p.endsWith('.java')) || paths.some(p => p.endsWith('pom.xml') || p.endsWith('build.gradle'))) return 'java';

  if (paths.some(p => p.endsWith('.cs')) || paths.some(p => p.endsWith('.csproj') || p.endsWith('.sln'))) return 'dotnet';

  if (paths.some(p => p.endsWith('.js'))) return 'vanilla';

  return 'unknown';
};

// GENERIC-FIX-2 — HTML/Static preview builder
const buildHtmlPreview = (files) => {
  let html = files['index.html'] ||
             files['/index.html'] ||
             Object.entries(files).find(([k]) => k.endsWith('index.html'))?.[1] || '';

  Object.entries(files).forEach(([path, content]) => {
    if (path.endsWith('.css')) {
      html = `<style>\n${content}\n</style>\n` + html;
    }
  });

  Object.entries(files).forEach(([path, content]) => {
    if (path.endsWith('.js') && !path.includes('node_modules')) {
      html = html + `\n<script>\n${content}\n</script>`;
    }
  });

  return html;
};

// GENERIC-FIX-3 — Sandpack template map
const SANDPACK_TEMPLATE_MAP = {
  react:   'react',
  vue:     'vue',
  vanilla: 'vanilla-ts',
  html:    'static',
  node:    'node',
};

// Single source of truth for Sandpack dependencies
// Sanitizer uses this to know what's installed vs what to stub
const SANDPACK_DEPS = {
  "react-router-dom": "6.20.0",
  "prop-types": "15.8.1",
  "axios": "1.6.0",
  "lucide-react": "0.294.0",
  "recharts": "2.10.0",
  "date-fns": "2.30.0",
  "clsx": "2.0.0",
  "zod": "3.22.0",
  "react-hook-form": "7.48.0",
  "@hookform/resolvers": "3.3.0",
  "zustand": "4.4.0",
  "framer-motion": "10.16.4",
};

const VIEWPORT_KEY = 'livepreview_viewport';
const VIEWPORTS = [
  { id: 'mobile',  label: 'Mobile (375px)',  icon: mobileIcon,  width: 375 },
  { id: 'tablet',  label: 'Tablet (768px)',  icon: tabletIcon,  width: 768 },
  { id: 'desktop', label: 'Desktop',         icon: desktopIcon, width: null },
];

// Custom navigator bar — replaces Sandpack's built-in one so we can insert
// viewport buttons between the refresh icon and the URL input field.
/** Listens for Sandpack runtime/compile errors and forwards to LogTab */
function SandpackErrorListener({ onRuntimeError }) {
  const { sandpack } = useSandpack();
  const reported = React.useRef(new Set());

  React.useEffect(() => {
    if (!onRuntimeError) return undefined;
    const unsub = sandpack.listen?.((msg) => {
      if (msg.type === 'action' && msg.action === 'show-error') {
        const key = `${msg.title || ''}:${msg.line || ''}`;
        if (reported.current.has(key)) return;
        reported.current.add(key);
        onRuntimeError({ message: msg.title || msg.message || 'Runtime error', file: msg.path, line: msg.line });
      }
      if (msg.type === 'console' && msg.log) {
        const errors = (Array.isArray(msg.log) ? msg.log : [msg.log]).filter(l => l.method === 'error');
        for (const err of errors) {
          const text = (err.data || []).join(' ').slice(0, 200);
          if (!text) continue;
          const key = text.slice(0, 80);
          if (reported.current.has(key)) continue;
          reported.current.add(key);
          onRuntimeError({ message: text });
        }
      }
    });
    return () => unsub?.();
  }, [sandpack, onRuntimeError]);

  return null;
}

function CustomNavigator({ viewport, onViewport }) {
  const { refresh, back, forward } = useSandpackNavigation();
  const { sandpack } = useSandpack();
  const [url, setUrl] = React.useState('/');

  React.useEffect(() => {
    const unsub = sandpack.listen?.((msg) => {
      if (msg.type === 'urlchange') setUrl(msg.url?.replace(/^https?:\/\/[^/]+/, '') || '/');
    });
    return () => unsub?.();
  }, []);

  return (
    <div style={navStyles.bar}>
      <button onClick={back}    style={navStyles.btn} title="Go back"><BackwardIcon /></button>
      <button onClick={forward} style={navStyles.btn} title="Go forward"><ForwardIcon /></button>
      <button onClick={refresh} style={navStyles.btn} title="Refresh"><RefreshIcon /></button>
      <input
        value={url}
        readOnly
        style={navStyles.input}
        aria-label="Current URL"
      />
      <div style={navStyles.vpGroup}>
        {VIEWPORTS.map(v => (
          <button
            key={v.id}
            onClick={() => onViewport(v.id)}
            title={v.label}
            style={{ ...navStyles.vpBtn, ...(v.id === viewport ? navStyles.vpBtnActive : {}) }}
          >
            <img
              src={v.icon}
              alt={v.label}
              style={{ width: 16, height: 16, objectFit: 'contain', display: 'block', filter: 'var(--vp-icon-filter)' }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// USE-SANDPACK-CRASH-FIX: CustomNavigator calls useSandpack()/
// useSandpackNavigation(), which throw ("must be wrapped by a
// SandpackProvider") unless mounted inside one. The real-dev-server
// preview path (a plain iframe, no SandpackProvider at all) renders its
// own toolbar outside that tree — confirmed live, this crashed the whole
// preview with exactly that error. This navigator has no Sandpack
// dependency: refresh just remounts the iframe (bumping reloadTick),
// back/forward are omitted since the parent can't drive history inside a
// genuinely cross-origin iframe (see PREVIEW-SUBDOMAIN-ORIGIN-ISOLATION-FIX).
function RealPreviewNavigator({ viewport, onViewport, url, onRefresh }) {
  return (
    <div style={navStyles.bar}>
      <button onClick={onRefresh} style={navStyles.btn} title="Refresh"><RefreshIcon /></button>
      <input
        value={url || ''}
        readOnly
        style={navStyles.input}
        aria-label="Current URL"
      />
      <div style={navStyles.vpGroup}>
        {VIEWPORTS.map(v => (
          <button
            key={v.id}
            onClick={() => onViewport(v.id)}
            title={v.label}
            style={{ ...navStyles.vpBtn, ...(v.id === viewport ? navStyles.vpBtnActive : {}) }}
          >
            <img
              src={v.icon}
              alt={v.label}
              style={{ width: 16, height: 16, objectFit: 'contain', display: 'block', filter: 'var(--vp-icon-filter)' }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// Listens for Sandpack bundler ready state — hides loader immediately when preview is ready
// Also handles stalled/errored states to prevent infinite loader
function SandpackReadyListener({ onReady }) {
  const { sandpack } = useSandpack();
  const readyFiredRef = React.useRef(false);

  React.useEffect(() => {
    if (readyFiredRef.current) return;
    const unsub = sandpack.listen?.((msg) => {
      // 'done' means bundler finished, 'resize' means iframe rendered
      if (msg.type === 'done' || msg.type === 'resize') {
        readyFiredRef.current = true;
        onReady();
      }
      // Handle error state — still show preview (error overlay is better than stuck loader)
      if (msg.type === 'action' && msg.action === 'show-error') {
        readyFiredRef.current = true;
        onReady();
      }
    });
    return () => unsub?.();
  }, []);

  // Also listen to sandpack status changes
  React.useEffect(() => {
    if (readyFiredRef.current) return;
    if (sandpack.status === 'idle' || sandpack.status === 'running') {
      // Give it a moment then force-show
      const t = setTimeout(() => {
        if (!readyFiredRef.current) {
          readyFiredRef.current = true;
          onReady();
        }
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [sandpack.status]);

  return null;
}

const navStyles = {
  bar: {
    display: 'flex', alignItems: 'center', gap: 2,
    height: 38, padding: '0 8px',
    background: 'var(--bg-card)',
    borderTop: '1px solid var(--border)',
    flexShrink: 0,
  },
  btn: {
    background: 'none', border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer', fontSize: 14, padding: '4px 6px', borderRadius: 5,
    lineHeight: 1, display: 'flex', alignItems: 'center',
    transition: 'color 0.15s, background 0.15s',
  },
  vpGroup: { display: 'flex', gap: 3, marginLeft: 8 },
  vpBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 14, padding: '3px 5px', borderRadius: 5,
    opacity: 0.35, transition: 'opacity 0.15s, background 0.15s',
    lineHeight: 1,
  },
  vpBtnActive: {
    opacity: 1,
    background: 'rgba(99,102,241,0.15)',
    boxShadow: '0 0 0 1px rgba(99,102,241,0.25)',
  },
  input: {
    flex: 1, marginLeft: 6,
    background: 'var(--bg-input)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 999, padding: '2px 12px', fontSize: 11,
    height: 22, outline: 'none',
    fontFamily: '"JetBrains Mono",monospace',
    letterSpacing: '0.01em',
  },
};

export default function LivePreview({ files, projectId, jobId, shareToken, onFilesUpdated, onRuntimeError, onElementEditRequest }) {
  const { mode } = useTheme();
  const sandpackTheme = mode === 'light' ? 'light' : 'dark';
  const [viewport, setViewportState] = useState(() => {
    const saved = localStorage.getItem(VIEWPORT_KEY);
    return VIEWPORTS.some(v => v.id === saved) ? saved : 'desktop';
  });
  const [previewLoading, setPreviewLoading] = useState(true);
  const lastWorkingFiles = useRef(null);
  // CLICK-TO-EDIT-FIX: only meaningful for the real-dev-server iframe
  // branch (isFullstackProject) — see that branch's own comment.
  const realIframeRef = useRef(null);
  const [editMode, setEditMode] = useState(false);
  const [pendingElementEdit, setPendingElementEdit] = useState(null); // {tag, className, id, text} | null
  const [elementEditText, setElementEditText] = useState('');
  // Silent auto-fix state (no UI indicators)
  const [fixAttempt, setFixAttempt] = useState(0);
  const [isFixing, setIsFixing] = useState(false);
  const autoFixCalled = useRef(false);
  const MAX_AUTO_FIX = 3;

  // DYNAMIC-PREVIEW-REAL-BACKEND: for a generated FE+BE+DB app, start the
  // project's REAL backend (sandbox/dynamic_preview.py — a real process,
  // not a browser simulation) once per project, then relay the Sandpack
  // iframe's /api/* fetch calls to it. The relay goes through postMessage
  // rather than a direct cross-origin fetch from inside the iframe,
  // because Sandpack's bundler serves the preview from ITS OWN dynamic
  // domain — a direct fetch would need that domain allow-listed in our
  // strict, no-wildcard CORS config (core/cors_config.py), which can't
  // target a domain that changes per session. Relaying through this
  // component instead means the actual network call is same-origin
  // (normal authFetch to our own API), so no CORS change is needed at
  // all. The injected mock script (below) falls back to its own
  // localStorage simulation if this relay doesn't answer in time, so a
  // slow/failed backend start never hangs the app.
  const hasDynamicBackend = useMemo(
    () => Object.keys(files || {}).some(p => p.startsWith('backend/')),
    [files]
  );
  const dynPreviewStarted = useRef(null); // tracks which projectId we've already called /start for
  // PUBLIC-SHARED-BACKEND-FIX: a `/shared/<token>` visitor has no login,
  // so the authenticated `/api/projects/{id}/dynamic-preview/*` routes
  // (and authFetch's token handling) don't apply — `shareToken` switches
  // both the URL and the fetch call to the public, share-token-authorized
  // counterpart (api/project_share.py) instead. Exactly one of
  // projectId/shareToken is ever set by the caller.
  const dynPreviewKey = projectId || shareToken;
  const dynPreviewBase = projectId
    ? `/api/projects/${projectId}/dynamic-preview`
    : shareToken ? `/api/shared/${shareToken}/dynamic-preview` : null;

  useEffect(() => {
    if (!hasDynamicBackend || !dynPreviewBase || dynPreviewStarted.current === dynPreviewKey) return;
    dynPreviewStarted.current = dynPreviewKey;
    (async () => {
      try {
        // AUTH-401-FIX: confirmed live — a raw fetch() with a manually-read
        // localStorage token 401'd permanently once the access token
        // expired mid-session, since (unlike authFetch) it never retries
        // after a silent refresh. The real backend then never started and
        // every page silently fell back to the mock/dummy data baked into
        // the generated components, with no visible error at all.
        // The public share-token path has no login at all, so it uses a
        // plain fetch — there's no token to refresh.
        const doFetch = projectId ? authFetch : fetch;
        await doFetch(`${dynPreviewBase}/start`, { method: 'POST' });
      } catch (e) { /* the relay below falls back to the mock on failure */ }
    })();
  }, [hasDynamicBackend, dynPreviewBase, dynPreviewKey, projectId]);

  useEffect(() => {
    if (!hasDynamicBackend || !dynPreviewBase) return;
    const onMessage = async (event) => {
      const msg = event.data;
      if (!msg || msg.__dynpreview !== true || !event.source) return;
      const { id, method, path, body, headers } = msg;
      try {
        // APP-AUTH-STRIP-FIX: confirmed live — the generated app's own
        // Authorization header (its OWN session token, from its OWN
        // login) was being lost twice over: authFetch below always
        // overwrites `Authorization` with the PLATFORM's token (it needs
        // that header for our own auth), and the backend proxy used to
        // strip `authorization` entirely before forwarding to the real
        // backend. Net effect: every authenticated call the generated
        // app made (anything past login) got a permanent 401 from its
        // OWN backend, even once that backend was running fine. Carry the
        // app's token through a header name that never collides with our
        // own auth; the backend proxy translates it back to a real
        // Authorization header before forwarding.
        const _appAuth = headers && (headers['Authorization'] || headers['authorization']);
        const doFetch = projectId ? authFetch : fetch;
        const res = await doFetch(`${dynPreviewBase}/proxy/${path}`, {
          method,
          headers: {
            ...(_appAuth ? { 'X-App-Authorization': _appAuth } : {}),
            ...(body ? { 'Content-Type': 'application/json' } : {}),
          },
          body: body || undefined,
        });
        const text = await res.text();
        event.source.postMessage({ __dynpreviewResponse: true, id, status: res.status, body: text }, '*');
      } catch (e) {
        event.source.postMessage({ __dynpreviewResponse: true, id, status: 0, body: '' }, '*');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [hasDynamicBackend, dynPreviewBase, projectId]);

  // REAL-DEV-SERVER-PREVIEW: for a fullstack project, use a REAL Vite
  // dev-server subprocess (api/dynamic_frontend_preview.py) instead of
  // Sandpack — exhaustive live testing proved Sandpack's `template="react"`
  // never executes ANY code this platform injects into it (confirmed with
  // a definitive window.__MARKER__ check across four different injection
  // mechanisms, all silently no-ops), which is why the old client-side
  // fetch mock never actually installed. A real dev server runs the
  // project's own unmodified code — including its own real dependency
  // versions, sidestepping the React-18-vs-19 API mismatches Sandpack's
  // forced dependency set caused too. Falls back to the existing Sandpack
  // path below on failure or for a frontend-only project.
  const isFullstackProject = useMemo(
    () => Object.keys(files || {}).some(p => p.startsWith('frontend/') || p.startsWith('backend/')),
    [files]
  );
  const [realPreview, setRealPreview] = useState({ status: 'idle', url: null });
  // The preview is served from a DIFFERENT origin (see the note in
  // startRealPreview). When that origin does not resolve, the iframe
  // renders the BROWSER's own error page — "server IP address could not
  // be found" — and this app never learns anything: no request of ours
  // failed, because the request never left the browser. A real dev
  // deployment sat like that, with generation, the API and the Code tab
  // all working, and nothing anywhere said why the preview was blank.
  // Probe the origin so the app can say it instead of the browser.
  const [previewOriginError, setPreviewOriginError] = useState(null);
  // BUILD-ERROR-IS-NOT-A-UI: Vite fills the preview with a raw
  // <vite-error-overlay> — exception text plus a @babel/parser stack
  // trace — whenever a module fails to transform. Confirmed live on
  // project 9505b8ee: the user's preview was fifteen lines of
  // node_modules paths. The preview runs on its own origin so this frame
  // cannot read or hide it; the shim injected by
  // dynamic_frontend_preview.py hides it there and posts the text here.
  const [previewBuildError, setPreviewBuildError] = useState(null);
  const realPreviewKey = projectId || shareToken;
  const realPreviewStarted = useRef(null);
  const [realPreviewReloadTick, setRealPreviewReloadTick] = useState(0);

  // STALE-DEAD-PREVIEW-FIX: confirmed live — the real dev-server process
  // this points at can die out from under an already-rendered iframe (the
  // registry TTL expires, or it gets evicted under the concurrent-preview
  // cap) with NO signal back to this component — realPreview.status just
  // stays 'running' forever, pointing at a URL nothing answers anymore.
  // The refresh button used to only bump realPreviewReloadTick (reload
  // the SAME dead src) — this pulled the actual /start call out of the
  // mount-time effect so the refresh button can re-run it for real,
  // spinning the process back up, instead of requiring the user to reload
  // the entire platform tab to get a fresh mount.
  const startRealPreview = React.useCallback(async () => {
    if (!isFullstackProject || !realPreviewKey) return;
    setRealPreview({ status: 'starting', url: null });
    try {
      const base = projectId
        ? `/api/projects/${projectId}/frontend-preview`
        : `/api/shared/${shareToken}/frontend-preview`;
      const doFetch = projectId ? authFetch : fetch;
      const res = await doFetch(`${base}/start`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (data.status === 'running') {
        // PREVIEW-SUBDOMAIN-ORIGIN-ISOLATION-FIX: served from a
        // dedicated subdomain (not this page's own origin) so the
        // iframe's `allow-same-origin` sandboxing can't be used by
        // untrusted generated JS to read this page's own
        // localStorage/login token — see nginx.conf's matching server
        // block for the full rationale.
        //
        // LOCALHOST-PREVIEW-PORT-FIX: the subdomain trick alone doesn't
        // work for local dev on literal "localhost" — confirmed live,
        // Chrome won't share a Domain=localhost cookie with a DIFFERENT
        // hostname like preview.localhost (it special-cases single-
        // label, non-public-suffix hosts), which breaks the owner-
        // authenticated preview's cookie auth specifically on this one
        // hostname. Cookies ignore PORT entirely though, so a dedicated
        // port on the exact same "localhost" hostname (nginx.conf +
        // docker-compose.yml's matching :3004 block) sidesteps it for
        // local testing only; any real domain always uses the subdomain
        // branch above, which already works correctly (verified against
        // a real multi-label domain).
        const { protocol, hostname, port } = window.location;
        const previewOrigin = hostname === 'localhost'
          ? `${protocol}//localhost:3004`
          : `${protocol}//preview.${hostname.replace(/^preview\./, '')}${port ? ':' + port : ''}`;
        const previewUrl = `${previewOrigin}${base}/proxy/`;
        setRealPreview({ status: 'running', url: previewUrl });
        // `no-cors` only tells us whether the browser could reach the
        // origin at all, which is exactly the question: a 404 or a 500
        // RESOLVES (opaque), while DNS failure, a TLS error or a refused
        // connection REJECT. The iframe is rendered either way and this
        // runs beside it, so a reachable preview is never delayed.
        // TWO TRIES BEFORE BLAMING THE HOST. A single blip would
        // otherwise replace a perfectly good preview with an
        // explanation of a problem that isn't there. A host that does
        // not resolve fails both times, instantly.
        setPreviewOriginError(null);
        // `credentials: 'include'` is not about the probe's verdict — an
        // opaque response tells us "reachable" either way. It is about
        // what the user sees: without it the browser sends no cookie to
        // the preview origin, the proxy answers 401, and the console
        // fills with red "401 (Unauthorized)" lines for a preview that
        // is working perfectly. Confirmed on project eb75cf37, where
        // those lines were being read as the cause of an unrelated
        // problem. With the cookie the same probe is an ordinary 200.
        const probe = () => fetch(previewUrl, {
          mode: 'no-cors', cache: 'no-store', credentials: 'include',
        });
        probe().catch(() =>
          new Promise((r) => setTimeout(r, 1500))
            .then(probe)
            .catch(() => setPreviewOriginError(previewOrigin))
        );
      } else {
        setRealPreview({ status: 'failed', url: null });
      }
    } catch (e) {
      setRealPreview({ status: 'failed', url: null });
    }
  }, [isFullstackProject, realPreviewKey, projectId, shareToken]);

  useEffect(() => {
    if (!isFullstackProject || !realPreviewKey || realPreviewStarted.current === realPreviewKey) return;
    realPreviewStarted.current = realPreviewKey;
    startRealPreview();
  }, [isFullstackProject, realPreviewKey, startRealPreview]);


  // Silent background auto-fix — small indicator only
  const triggerAutoFix = async (errorMessage) => {
    if (fixAttempt >= MAX_AUTO_FIX || !projectId) return;
    setIsFixing(true);
    try {
      // AUTH-401-FIX: same bug as the dynamic-preview calls above — a raw
      // fetch() with a manually-read token never recovers once the access
      // token expires mid-session. authFetch retries once after a silent
      // refresh, same as every other authenticated call in this app.
      const res = await authFetch(`/api/projects/${projectId}/fix-error`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ error_message: errorMessage, job_id: jobId || '', attempt: fixAttempt + 1 }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'fixed' && data.files_updated?.length > 0) {
          setFixAttempt(prev => prev + 1);
          if (onFilesUpdated) onFilesUpdated(); // Silently refresh files
          // REAL-PREVIEW-SYNC-AFTER-FIX-FIX: confirmed live — fix-error's
          // write-back lands in the DB, but the real dev-server iframe
          // reads its OWN already-running workspace on disk
          // (/tmp/dynfrontend_*), which nothing was ever telling to pick
          // up the change — the fix silently never appeared in the
          // iframe the user was looking at. onFilesUpdated only refreshes
          // this component's `files` prop from the DB, which the real
          // iframe doesn't re-read reactively the way Sandpack does.
          if (isFullstackProject && realPreview.status === 'running') {
            try {
              const base = projectId
                ? `/api/projects/${projectId}/frontend-preview`
                : `/api/shared/${shareToken}/frontend-preview`;
              const doFetch = projectId ? authFetch : fetch;
              await doFetch(`${base}/sync`, { method: 'POST' });
              setRealPreviewReloadTick(t => t + 1);
            } catch (e) { /* non-fatal — next manual refresh still shows the fix */ }
          }
        }
      }
    } catch (e) { /* silent */ }
    finally { autoFixCalled.current = false; setIsFixing(false); }
  };

  // RUNTIME-ERROR-FORWARD-FIX: the real-dev-server iframe is a genuinely
  // separate origin (preview.* subdomain / local :3004 port — see
  // PREVIEW-SUBDOMAIN-ORIGIN-ISOLATION-FIX elsewhere in this file), so an
  // uncaught error inside it (e.g. a generated page crashing on
  // `undefined.toLocaleString()`) can only ever reach this parent page via
  // postMessage — dynamic_frontend_preview.py's injected shim now sends
  // one. Without this listener, that error had nowhere to go: no Live Log
  // entry, and — unlike the Sandpack path's SandpackErrorListener — no
  // chance of ever reaching the SAME silent auto-fix loop used there.
  React.useEffect(() => {
    function onRuntimeErrorMessage(event) {
      const msg = event.data;
      if (!msg || msg.__previewRuntimeError !== true) return;
      if (onRuntimeError) onRuntimeError({ message: msg.message, file: msg.file, line: msg.line });
      if (projectId && !autoFixCalled.current && fixAttempt < MAX_AUTO_FIX) {
        autoFixCalled.current = true;
        triggerAutoFix(`${msg.file ? msg.file + ': ' : ''}${msg.message}`);
      }
    }
    window.addEventListener('message', onRuntimeErrorMessage);
    return () => window.removeEventListener('message', onRuntimeErrorMessage);
  }, [projectId, shareToken, fixAttempt, isFullstackProject, realPreview.status]);

  // The same postMessage channel, for a build-time failure rather than a
  // runtime one: the app never got far enough to throw.
  React.useEffect(() => {
    function onBuildErrorMessage(event) {
      const msg = event.data;
      if (!msg) return;
      if (msg.__dynpreviewBuildErrorCleared === true) { setPreviewBuildError(null); return; }
      if (typeof msg.__dynpreviewBuildError !== 'string') return;
      setPreviewBuildError(msg.__dynpreviewBuildError);
    }
    window.addEventListener('message', onBuildErrorMessage);
    return () => window.removeEventListener('message', onBuildErrorMessage);
  }, []);

  // CLICK-TO-EDIT-FIX: same postMessage channel, the shim's own click
  // handler (sandbox/dynamic_frontend_preview.py) only reports an element
  // while edit mode is armed — see the toggle button near the iframe below.
  React.useEffect(() => {
    function onElementClickMessage(event) {
      const msg = event.data;
      if (!msg || msg.__dynpreviewElementClick !== true) return;
      setPendingElementEdit({ tag: msg.tag, className: msg.className, id: msg.id, text: msg.text });
      setElementEditText('');
    }
    window.addEventListener('message', onElementClickMessage);
    return () => window.removeEventListener('message', onElementClickMessage);
  }, []);

  // Re-send the current edit-mode flag whenever the iframe reloads (a new
  // iframe element = a fresh shim instance with __editMode reset to false).
  React.useEffect(() => {
    if (!editMode) return;
    const win = realIframeRef.current?.contentWindow;
    if (win) win.postMessage({ __dynpreviewEditMode: true }, '*');
  }, [editMode, realPreviewReloadTick]);

  const toggleEditMode = () => {
    const next = !editMode;
    setEditMode(next);
    const win = realIframeRef.current?.contentWindow;
    if (win) win.postMessage({ __dynpreviewEditMode: next }, '*');
    if (!next) { setPendingElementEdit(null); }
  };

  const submitElementEdit = () => {
    if (!pendingElementEdit || !elementEditText.trim() || !onElementEditRequest) return;
    const { tag, className, id, text } = pendingElementEdit;
    const descriptor = [
      `<${tag}`,
      className ? ` class="${className}"` : '',
      id ? ` id="${id}"` : '',
      '>',
      text || '',
    ].join('');
    const prompt = `On the current page, for this specific element: ${descriptor} — ${elementEditText.trim()}`;
    onElementEditRequest(prompt);
    setPendingElementEdit(null);
    setElementEditText('');
  };

  // Reset fix state when files change
  const filesFingerprint = files ? Object.keys(files).length : 0;
  React.useEffect(() => { setFixAttempt(0); autoFixCalled.current = false; }, [filesFingerprint]);

  const handleViewport = (id) => {
    setViewportState(id);
    localStorage.setItem(VIEWPORT_KEY, id);
  };

  const vp = VIEWPORTS.find(v => v.id === viewport);
  // Dependency key for useMemo — must change when ANY file content changes
  // Uses simple hash of all content to detect even single-character changes
  const filesKey = useMemo(() => {
    // SAMPLED-HASH-STALE-PREVIEW-FIX: this used to sample only every 50th
    // character (`i += 50`) — a small edit landing between two sampled
    // offsets (e.g. `detail="x"` -> `detail: 'x'`, a 2-char change) could
    // leave the hash completely unchanged. Confirmed live: a real,
    // server-confirmed file fix never reached Sandpack because this
    // memo's dependency (filesKey) didn't change, so sandpackConfig's own
    // useMemo below never recomputed — Sandpack kept bundling the OLD
    // in-memory files object indefinitely, surviving hard refreshes and
    // even incognito windows (since the stale value lived in this
    // render's memoized state, not any browser cache). Hash every
    // character instead — for this app's realistic project sizes (tens to
    // low hundreds of KB total) this is still sub-millisecond, and it
    // only runs when the `files` object reference actually changes.
    if (!files) return '';
    let hash = 0;
    const entries = Object.entries(files);
    for (const [path, content] of entries) {
      for (let i = 0; i < content.length; i++) {
        hash = ((hash << 5) - hash + content.charCodeAt(i)) | 0;
      }
      hash = ((hash << 5) - hash + path.length) | 0;
    }
    return entries.length + '|' + hash;
  }, [files]);

  // SYNC-ON-EDIT-FIX: confirmed live — a chat "enhancement" prompt on an
  // ALREADY-OPEN preview completed successfully (tokens consumed, files
  // persisted to the DB, `onFilesUpdated` fired) but the real dev-server
  // iframe never visually changed. Root cause: nothing told the already-
  // running workspace to pick up the new files — the earlier
  // STALE-WORKSPACE-ON-REUSE fix only re-syncs on the NEXT `/start` call
  // (a fresh mount/reopen), and the only existing post-edit sync
  // (REAL-PREVIEW-SYNC-AFTER-FIX-FIX above) only fires after a runtime-
  // error auto-fix, not after a normal successful edit. This watches the
  // same `filesKey` fingerprint used for the Sandpack path and, once it
  // changes after the initial load, re-syncs the frontend workspace
  // (cheap file write) and re-hits the backend start endpoint (a no-op
  // unless the backend's content hash actually changed, so it's safe to
  // call on every edit) before bumping the reload tick.
  const prevSyncFilesKeyRef = useRef(null);
  useEffect(() => {
    if (prevSyncFilesKeyRef.current === null) {
      prevSyncFilesKeyRef.current = filesKey;
      return;
    }
    if (prevSyncFilesKeyRef.current === filesKey) return;
    prevSyncFilesKeyRef.current = filesKey;
    if (!isFullstackProject || realPreview.status !== 'running') return;
    (async () => {
      try {
        const projectBase = projectId ? `/api/projects/${projectId}` : `/api/shared/${shareToken}`;
        const doFetch = projectId ? authFetch : fetch;
        await doFetch(`${projectBase}/frontend-preview/sync`, { method: 'POST' });
        await doFetch(`${projectBase}/dynamic-preview/start`, { method: 'POST' }).catch(() => {});
        setRealPreviewReloadTick(t => t + 1);
      } catch (e) { /* non-fatal — manual refresh still shows the edit */ }
    })();
  }, [filesKey, isFullstackProject, realPreview.status, projectId, shareToken]);

  const sandpackConfig = useMemo(() => {
    if (!files || Object.keys(files).length === 0) return null;
    // SKIP-WASTED-SANDPACK-PREP-FIX: confirmed live — this whole block
    // (Babel AST validation, AI sanitizer, silent auto-fix triggering)
    // used to run unconditionally on every files change EVEN for
    // fullstack projects rendering the real-dev-server iframe, whose
    // render branch never uses this value except as an unused fallback.
    // That meant wasted Babel-parse + LLM auto-fix calls kept firing
    // against the raw source (a syntax-only check, blind to what the real
    // iframe is actually doing) and polluted the console with "Babel
    // found errors"/fix-error noise totally unrelated to what the user
    // was looking at. Only skip while the real preview is actually usable
    // (running/starting/idle) — a 'failed' real preview still needs this
    // to build the Sandpack fallback.
    if (isFullstackProject && realPreview.status !== 'failed') return null;

    const spFiles = {};
    const cssFiles = [];
    const componentFiles = [];

    for (const [path, content] of Object.entries(files)) {
      // Skip non-frontend files
      if (path.endsWith('.py') || path.endsWith('.java') || path.endsWith('.go') || path.endsWith('.cs')) continue;
      if (path.includes('.test.') || path.includes('.spec.') || path.startsWith('tests/')) continue;
      if (path === 'Dockerfile' || path.endsWith('.yml') || path.endsWith('.yaml') || path.endsWith('.md')) continue;
      if (path === 'package.json' || path === 'vite.config.js' || path === '.env.example' || path === '.gitignore' || path === 'README.md') continue;
      if (path.endsWith('.ts') && !path.endsWith('.tsx') && (path.includes('types') || path.includes('lib/'))) continue;
      // Skip backend/server files — these are Node.js server code, not browser code
      if (path.includes('backend/') || path.includes('database')) continue;
      if (path.includes('server.js') || path.includes('server.ts')) continue;
      // FRONTEND-DIR-NAME-COLLISION-FIX (real user report, job 41397b20):
      // the directory filters below exist to drop BACKEND folders, but they
      // matched any path CONTAINING those names — so the frontend's own
      // `frontend/src/config/navConfig.js` was silently excluded from the
      // preview. Layout.js imports { brand, navLinks } from it, the stub
      // creator further down then invented
      // `export const navLinks = () => null`, and (navLinks || []).filter
      // threw "filter is not a function" because a FUNCTION is truthy, so
      // the `|| []` fallback never engaged. The whole app rendered
      // "Something went wrong". `routes/`, `models/`, `middleware/` and
      // `controllers/` are all perfectly normal frontend folder names too,
      // so the exclusion must be scoped to files that are NOT part of the
      // frontend source tree.
      const isFrontendSource = /^(frontend\/)?src\//.test(path) || path.startsWith('frontend/');
      if (!isFrontendSource) {
        if (path.includes('models/') || path.includes('routes/') || path.includes('repositories/')) continue;
        if (path.includes('middleware/') || path.includes('controllers/') || path.includes('config/')) continue;
      }
      // Skip ANY service file that contains backend patterns (regardless of path)
      if (path.includes('services/') || path.includes('service')) {
        const isBackendService = (
          content.includes('bcrypt') || content.includes('jwt') || content.includes('jsonwebtoken') ||
          content.includes('mongoose') || content.includes('sequelize') || content.includes('prisma') ||
          content.includes('knex') || content.includes('pg') || content.includes('mysql') ||
          content.includes('uuid') || content.includes('crypto') ||
          content.includes('req,') || content.includes('req.body') || content.includes('res.json') ||
          content.includes('module.exports') || content.includes('exports.') ||
          content.includes('findAll') || content.includes('findById') || content.includes('findOne') ||
          content.includes('INSERT INTO') || content.includes('SELECT ') || content.includes('DELETE FROM')
        );
        // Only allow through if it looks like a frontend API helper (axios/fetch calls)
        const isFrontendApi = (
          content.includes('axios') || content.includes('fetch(') ||
          content.includes('baseURL') || content.includes('API_URL') ||
          content.includes('localStorage') || content.includes('Bearer')
        );
        if (isBackendService && !isFrontendApi) continue;
      }
      // Skip files that use require() with Node.js server modules
      if (content.includes('require(') && (content.includes('mongoose') || content.includes('express') || content.includes('http') || content.includes('bcrypt') || content.includes('jsonwebtoken') || content.includes('uuid'))) continue;
      // Skip Express app files even if named app.js
      if (content.includes('express()') || content.includes('app.listen(')) continue;
      // Skip files that import Node.js-only packages (clear backend indicator)
      if (content.includes("from 'bcrypt'") || content.includes("from 'uuid'") ||
          content.includes("from 'jsonwebtoken'") || content.includes("from 'mongoose'") ||
          content.includes("from 'pg'") || content.includes("from 'sequelize'") ||
          content.includes("from 'crypto'") || content.includes("from 'fs'") ||
          content.includes("from 'path'") || content.includes("from 'http'")) continue;
      if (content.includes("from 'bcrypt'") || content.includes("from 'uuid'") ||
          content.includes("from 'jsonwebtoken'") || content.includes("from 'mongoose'") ||
          content.includes("from 'pg'") || content.includes("from 'sequelize'")) continue;

      // Normalize path
      let np = path
        .replace(/^frontend\/src\//, '/')
        .replace(/^frontend\//, '/')
        .replace(/^src\//, '/');
      if (!np.startsWith('/')) np = '/' + np;

      let code = content;

      // GENERIC-VALIDATOR-FIX: Frontend fallback validation
      // Remove markdown fences
      code = code.replace(/^```[\w]*\s*\n?/gm, '').replace(/^```\s*$/gm, '').replace(/```\s*$/, '').trim();
      // Fix @/ aliases
      code = code.replace(/from ['"]@\/(components|pages|hooks|services|utils|context|lib|store)\//g, "from '/$1/");
      code = code.replace(/import.*from ['"]@\/types\/[^'"]+['"];?\n?/g, '');
      // Keep <Router> as-is — the FINAL SAFETY block below will ensure it's imported
      // Remove BrowserRouter from Layout files
      if (np.includes('layout') || np.includes('Layout')) {
        code = code.replace(/<BrowserRouter>/g, '').replace(/<\/BrowserRouter>/g, '');
      }
      // Fix ROUTES undefined
      if (code.includes('ROUTES.') && !code.includes('const ROUTES') && !code.includes('export const ROUTES')) {
        const routeKeys = [...new Set([...code.matchAll(/ROUTES\.([A-Z_]+)/g)].map(m => m[1]))];
        if (routeKeys.length > 0) {
          const routeMap = {HOME:'/',LANDING:'/',LOGIN:'/login',SIGNUP:'/signup',REGISTER:'/register',DASHBOARD:'/dashboard',PROFILE:'/profile',SETTINGS:'/settings',PRODUCTS:'/products',CART:'/cart',ORDERS:'/orders',ADMIN:'/admin',ABOUT:'/about',CONTACT:'/contact'};
          const routesConst = `const ROUTES = {\n${routeKeys.map(k => `  ${k}: "${routeMap[k] || '/'+k.toLowerCase().replace(/_/g,'-')}"` ).join(',\n')}\n};\n\n`;
          code = code.replace(/import\s+\{[^}]*ROUTES[^}]*\}\s+from\s+['"][^'"]+['"];?\s*\n?/g, '');
          code = code.replace(/import\s+ROUTES\s+from\s+['"][^'"]+['"];?\s*\n?/g, '');
          const lastImport = [...code.matchAll(/^import\s+.+$/gm)].pop();
          if (lastImport) { const pos = lastImport.index + lastImport[0].length; code = code.slice(0, pos) + '\n\n' + routesConst + code.slice(pos); }
          else { code = routesConst + code; }
        }
      }
      // Strip TypeScript from .js
      if (np.endsWith('.js')) {
        // TERNARY/OBJECT-VALUE-FIX: was un-anchored and included null|undefined, so
        // `cond ? a : null;` lost its `: null` and `{ x: null, }` became `{ x, }`.
        // Only strip a real `identifier: primitiveType` pair, never inside a `? :`.
        code = code.replace(/([A-Za-z_$][\w$]*)\s*:\s*(string|number|boolean|any|void|never)(\[\])?\s*([,;)=])/g, (m, id, t, arr, tail, off, str) => {
          const st = Math.max(str.lastIndexOf(';', off - 1), str.lastIndexOf('{', off - 1), str.lastIndexOf('}', off - 1)) + 1;
          return str.slice(st, off).includes('?') ? m : id + tail;
        });
        code = code.replace(/:\s*React\.FC(\<[^>]*\>)?/g, '');
        code = code.replace(/interface\s+\w+\s*\{[^}]*\}/gs, '').replace(/type\s+\w+\s*=\s*[^;]+;/g, '');
        code = code.replace(/useState<[^>]+>/g, 'useState').replace(/useRef<[^>]+>/g, 'useRef');
      }

      // Skip diff-format files
      if (code.startsWith('---') || code.startsWith('diff ') || code.startsWith('DIFF:')) {
        continue;
      }

      const lines = code.split('\n');
      if (lines[0] && lines[0].match(/^[a-zA-Z_/\\][a-zA-Z0-9_/.\-]*\.(js|jsx|ts|tsx|css|py)$/)) {
        code = lines.slice(1).join('\n');
      }
      // Remove CSS imports for files not in generated output
      code = code.replace(/^import\s+['"]\.\/[^'"]*\.css['"];?\s*$/gm, (match) => {
        const cssFile = match.match(/['"]\.\/([^'"]+\.css)['"]/)?.[1];
        if (cssFile) {
          const dir = np.substring(0, np.lastIndexOf('/'));
          const fullCssPath = dir + '/' + cssFile;
          if (!Object.keys(files).some(f => {
            let normalized = f.replace(/^frontend\/src\//, '/').replace(/^frontend\//, '/').replace(/^src\//, '/');
            if (!normalized.startsWith('/')) normalized = '/' + normalized;
            return normalized === fullCssPath;
          })) {
            return '// CSS import removed (file not generated)';
          }
        }
        return match;
      });

      spFiles[np] = code;

      if (np.endsWith('.css')) {
        // MARKET-STANDARD-FIX-V2: Strip @tailwind/@apply for Sandpack (CDN handles it)
        spFiles[np] = spFiles[np].replace(/@tailwind\s+[^;]+;\s*/g, '').replace(/@apply\s+[^;]+;/g, '/* $& */');
        cssFiles.push(np);
      }
      else if (np.match(/\.(js|jsx|ts|tsx)$/)) componentFiles.push(np);
    }

    if (componentFiles.length === 0) return null;

    // PREVIEW-BACKEND-MOCK: For fullstack projects, the real backend runs on
    // Node.js and can't run in Sandpack's browser sandbox. Inject a fetch
    // interceptor that handles /api/* calls with localStorage so login/
    // signup/CRUD all work in the preview without a running server. The
    // real backend code is untouched — this only affects Sandpack.
    //
    // DYNAMIC-MOCK-GATE-FIX: this only checked for a 'frontend/' prefix,
    // which matches the single-shot path's layout (stack upgraded to
    // react-node BEFORE generation, producing frontend/+backend/ from the
    // start) but NOT the chunked path's layout, where dynamic_generator.py
    // bolts real backend/ files onto an already-generated frontend that
    // still sits at plain src/ (no 'frontend/' prefix at all) — confirmed
    // live: a chunked dynamic-app generation had zero 'frontend/'-prefixed
    // paths, so this mock never activated and every /api/* fetch call in
    // preview (signup, login, ...) failed outright. Detecting by the
    // presence of backend/ files instead (which both layouts always have
    // when a real backend was generated) covers both.
    const isFullstack = Object.keys(files).some(p => p.startsWith('frontend/') || p.startsWith('backend/'));
    if (isFullstack) {
      const mockScript = `
// PREVIEW BACKEND MOCK — intercepts /api/* fetch calls in Sandpack preview
// Real backend code is unchanged. This only runs in the browser preview.
(function() {
  const _realFetch = window.fetch.bind(window);
  const DB = {
    get: (k) => { try { return JSON.parse(localStorage.getItem('_mock_' + k) || 'null'); } catch { return null; } },
    set: (k, v) => { try { localStorage.setItem('_mock_' + k, JSON.stringify(v)); } catch {} },
  };
  function ok(data, status) {
    return Promise.resolve(new Response(JSON.stringify(data), {
      status: status || 200,
      headers: { 'Content-Type': 'application/json' },
    }));
  }
  function err(msg, status) {
    return Promise.resolve(new Response(JSON.stringify({ error: msg }), {
      status: status || 400,
      headers: { 'Content-Type': 'application/json' },
    }));
  }
  function parseBody(init) {
    try { return JSON.parse((init && init.body) || '{}'); } catch { return {}; }
  }
  function makeJwt(payload) {
    // Fake JWT — just base64 encode the payload so frontend can decode it
    return 'mock.' + btoa(JSON.stringify(payload)) + '.sig';
  }
  function verifyJwt(token) {
    try { return JSON.parse(atob((token || '').split('.')[1])); } catch { return null; }
  }
  function getAuthUser(init) {
    const auth = (init && init.headers && (init.headers['Authorization'] || init.headers['authorization'])) || '';
    const token = auth.replace('Bearer ', '');
    return verifyJwt(token);
  }

  // REAL-BACKEND-RELAY: try the project's actual running backend first
  // (via the parent page — see LivePreview.js's postMessage listener),
  // and only fall back to the localStorage simulation below if it
  // doesn't answer within RELAY_TIMEOUT_MS (backend still starting,
  // failed to start, or an older cached bundle with no listener yet).
  const RELAY_TIMEOUT_MS = 8000;
  let _relaySeq = 0;
  function tryRealBackend(path, method, rawBody, init) {
    return new Promise((resolve) => {
      const id = 'dp' + (++_relaySeq) + '_' + Date.now();
      const headers = {};
      const h = (init && init.headers) || {};
      if (h['Authorization'] || h['authorization']) headers['Authorization'] = h['Authorization'] || h['authorization'];
      let settled = false;
      const onMsg = (event) => {
        const msg = event.data;
        if (!msg || msg.__dynpreviewResponse !== true || msg.id !== id) return;
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMsg);
        if (msg.status === 0) { resolve(null); return; }
        // NON-JSON-RELAY-GUARD: the Content-Type header above is forced to
        // 'application/json' regardless of what msg.body actually is — if
        // anything between here and the real backend (a mid-startup race,
        // a gateway hiccup) ever returns an HTML error page instead of
        // JSON, this used to hand that straight to the app as if it were
        // a real, successful JSON response, and the app's own res.json()
        // call would throw "Unexpected token '<' ... is not valid JSON".
        // A relay response claiming to be real should actually BE valid
        // JSON (or empty); anything else is treated as a failed relay so
        // localMock runs instead, same as a genuine timeout would.
        const _body = msg.body || '';
        if (_body && !/^\s*[{[]/.test(_body)) { resolve(null); return; }
        resolve(new Response(msg.body, {
          status: msg.status,
          headers: { 'Content-Type': 'application/json' },
        }));
      };
      window.addEventListener('message', onMsg);
      window.parent.postMessage({
        __dynpreview: true, id, method,
        path: path.replace(/^\/api\//, ''),
        body: rawBody != null ? JSON.stringify(rawBody) : undefined,
        headers,
      }, '*');
      setTimeout(() => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMsg);
        resolve(null);
      }, RELAY_TIMEOUT_MS);
    });
  }

  function localMock(path, method, body, init) {
    const segments = path.replace('/api/', '').split('/');
    const resource = segments[0];
    const id = segments[1];

    // ── AUTH ──────────────────────────────────────────────────────────────
    if (resource === 'auth') {
      const action = segments[1];

      if (action === 'register' && method === 'POST') {
        const { email, password, name } = body;
        if (!email || !password) return err('Email and password required');
        const users = DB.get('users') || [];
        if (users.find(u => u.email === email)) return err('Email already registered');
        const user = { id: Date.now(), email, password, name: name || email.split('@')[0], role: 'user', created_at: new Date().toISOString() };
        users.push(user);
        DB.set('users', users);
        const token = makeJwt({ id: user.id, email: user.email, role: user.role });
        return ok({ token, user: { id: user.id, email: user.email, name: user.name, role: user.role } });
      }

      if (action === 'login' && method === 'POST') {
        const { email, password } = body;
        const users = DB.get('users') || [];
        const user = users.find(u => u.email === email);
        if (!user || user.password !== password) return err('Invalid credentials', 401);
        const token = makeJwt({ id: user.id, email: user.email, role: user.role });
        return ok({ token, user: { id: user.id, email: user.email, name: user.name, role: user.role } });
      }

      if (action === 'me' && method === 'GET') {
        const u = getAuthUser(init);
        if (!u) return err('Unauthorized', 401);
        const users = DB.get('users') || [];
        const user = users.find(x => x.id === u.id);
        return user ? ok({ id: user.id, email: user.email, name: user.name, role: user.role }) : err('Not found', 404);
      }
    }

    // ── GENERIC CRUD for any /api/<resource> ──────────────────────────────
    const items = DB.get(resource) || [];

    if (method === 'GET' && !id) {
      return ok(items);
    }
    if (method === 'GET' && id) {
      const item = items.find(x => String(x.id) === String(id));
      return item ? ok(item) : err('Not found', 404);
    }
    if (method === 'POST') {
      const newItem = { ...body, id: Date.now(), created_at: new Date().toISOString() };
      items.push(newItem);
      DB.set(resource, items);
      return ok(newItem, 201);
    }
    if ((method === 'PUT' || method === 'PATCH') && id) {
      const idx = items.findIndex(x => String(x.id) === String(id));
      if (idx === -1) return err('Not found', 404);
      items[idx] = { ...items[idx], ...body, updated_at: new Date().toISOString() };
      DB.set(resource, items);
      return ok(items[idx]);
    }
    if (method === 'DELETE' && id) {
      const idx = items.findIndex(x => String(x.id) === String(id));
      if (idx === -1) return err('Not found', 404);
      items.splice(idx, 1);
      DB.set(resource, items);
      return ok({ success: true });
    }

    return err('Not found', 404);
  }

  window.fetch = async function(url, init) {
    const path = typeof url === 'string' ? url : (url.url || '');
    if (!path.startsWith('/api/')) return _realFetch(url, init);
    const method = ((init && init.method) || 'GET').toUpperCase();
    const body = parseBody(init);
    const real = await tryRealBackend(path, method, (init && init.body) ? body : null, init);
    if (real) return real;
    return localMock(path, method, body, init);
  };
})();
`;
      spFiles['/preview-backend-mock.js'] = mockScript;
      // Inject the mock into the entry file so it runs before any app code.
      // ENTRY-EXT-FIX: this only checked the .js spelling of the entry
      // file, but every project this platform generates names it
      // main.jsx (confirmed live on two separate dynamic-app generations)
      // — the same four spellings already handled elsewhere in this file
      // (see hasRootIndex below) are needed here too, or the mock silently
      // never gets injected at all, regardless of the isFullstack check
      // above.
      const indexKey = ['/index.js', '/main.js', '/main.jsx', '/index.jsx'].find(k => spFiles[k]);
      if (indexKey) {
        spFiles[indexKey] = `import './preview-backend-mock.js';\n` + spFiles[indexKey];
      }
      // APP-ENTRY-MOCK-FIX: confirmed live (via direct evaluate() inside
      // the actual preview document) that Sandpack's `template="react"`
      // NEVER runs any index/main file we provide — it always executes
      // its OWN built-in default `/index.js` (plain `import App from
      // './App'; createRoot(...).render(<App/>)`), regardless of
      // `files['/index.js']` content OR `customSetup.entry`. What IS
      // provably real is `/App.js`'s own content — confirmed by reading
      // `document.getElementById('root').innerHTML` from inside the live
      // preview and finding this project's actual rendered markup there.
      // Injecting the mock import into `/App.js` instead targets the one
      // file empirically guaranteed to execute before anything renders,
      // sidestepping the entry-file guessing game entirely.
      const appKey = Object.keys(spFiles).find(k => /^\/App\.(js|jsx)$/.test(k));
      if (appKey && !spFiles[appKey].includes('preview-backend-mock')) {
        spFiles[appKey] = `import './preview-backend-mock.js';\n` + spFiles[appKey];
      }
      // INLINE-SCRIPT-TEMPLATE-FIX: the import-chain injection above
      // assumes Sandpack's `template="react"` bundler actually treats
      // OUR `/index.js`/`/main.js` as its real entry — confirmed live it
      // does NOT: the rendered preview document is titled "Sandbox -
      // CodeSandbox" (Sandpack's own built-in `/public/index.html`
      // default), not anything from this project, meaning the template's
      // fixed internal environment is using its OWN bootstrap rather
      // than ours — so `window.fetch` was never actually overridden
      // (verified live: `window.fetch.toString()` inside the preview
      // still showed native code after the import-chain fix), and every
      // `/api/*` call fell straight through to the real network, hitting
      // the Sandpack bundler's own hosting origin and getting back its
      // generic HTML shell — "Unexpected token '<' ... is not valid
      // JSON" on every signup/login, consistently. A raw inline
      // `<script>` placed directly in the HTML `<head>` sidesteps this
      // entirely — it executes at HTML-parse time, before ANY bundled JS
      // runs, completely independent of which file Sandpack's chosen
      // template treats as "the" entry point.
      const inlineMockTag = `<script>\n${mockScript.replace(/<\/script>/gi, '<\\/script>')}\n</script>`;
      const baseHtml = spFiles['/index.html'] || spFiles['/public/index.html']
        || '<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Preview</title></head><body><div id="root"></div></body></html>';
      const htmlWithMock = /<head[^>]*>/i.test(baseHtml)
        ? baseHtml.replace(/<head[^>]*>/i, (m) => `${m}\n${inlineMockTag}\n`)
        : `${inlineMockTag}\n${baseHtml}`;
      spFiles['/index.html'] = htmlWithMock;
      spFiles['/public/index.html'] = htmlWithMock;
    }

    // Fix: Ensure App.jsx has a default "/" route
    const appFilePath = componentFiles.find(f => f.match(/\/App\.(js|jsx|ts|tsx)$/));
    if (appFilePath && spFiles[appFilePath]) {
      let appCode = spFiles[appFilePath];
      if (appCode.includes('<Routes') && !appCode.match(/path=["']\/["']/)) {
        const firstRouteMatch = appCode.match(/(<Route\s+path=["'][^"']+["']\s+element=\{[^}]+\}\s*\/>)/);
        if (firstRouteMatch) {
          const firstRoute = firstRouteMatch[1];
          const defaultRoute = firstRoute.replace(/path=["'][^"']+["']/, 'path="/"');
          appCode = appCode.replace(firstRoute, defaultRoute + '\n          ' + firstRoute);
          spFiles[appFilePath] = appCode;
        }
      }

      // Fix: Ensure Layout wraps Routes as children (not siblings)
      // Pattern: <Layout /> <Routes>...</Routes> → <Layout><Routes>...</Routes></Layout>
      if (appCode.includes('<Layout') && appCode.includes('<Routes') && !appCode.includes('<Layout>')) {
        // Layout is self-closing but Routes exist separately — wrap Routes inside Layout
        appCode = appCode.replace(/<Layout\s*\/>/g, '');
        appCode = appCode.replace(/(<Routes[\s\S]*?<\/Routes>)/, '<Layout>\n        $1\n      </Layout>');
        spFiles[appFilePath] = appCode;
      }

      // Fix: Ensure Layout wraps Routes as children (not siblings)
      if (appCode.includes('<Layout') && appCode.includes('<Routes') && !appCode.includes('<Layout>')) {
        appCode = appCode.replace(/<Layout\s*\/>/g, '');
        appCode = appCode.replace(/(<Routes[\s\S]*?<\/Routes>)/, '<Layout>\n        $1\n      </Layout>');
        spFiles[appFilePath] = appCode;
      }
    }

    // Rename ALL .jsx/.tsx/.ts files to .js for Sandpack resolution
    const renamedFiles = {};
    for (const [fp, code] of Object.entries(spFiles)) {
      const newPath = fp.replace(/\.(jsx|tsx|ts)$/, '.js');
      renamedFiles[newPath] = code;
    }
    Object.keys(spFiles).forEach(k => delete spFiles[k]);
    Object.assign(spFiles, renamedFiles);
    componentFiles.length = 0;
    componentFiles.push(...Object.keys(spFiles).filter(f => f.match(/\.(js|ts)$/)));

    // VUE-SKIP-FIX APPLIED — skip React-specific processing for non-React frameworks
    const detectedFw = detectFramework(files);
    if (detectedFw === 'vue' || detectedFw === 'html') {
      return spFiles;
    }

    // GENERIC: derive allowed imports from SANDPACK_DEPS (single source of truth)
    const ALLOWED_IMPORTS = ["react", "react-dom", ...Object.keys(SANDPACK_DEPS)];

    // Helper: check if a package is actually installed (exact package match, not prefix)
    const isInstalledPackage = (pkg) => {
      return ALLOWED_IMPORTS.some(a => pkg === a || pkg.startsWith(a + '/'));
    };





















    for (const fp of componentFiles) {
      if (!spFiles[fp]) continue;

      // Auto-inject missing React import
      if (!spFiles[fp].match(/import\s+React/) && !spFiles[fp].startsWith('const ')) {
        spFiles[fp] = 'import React from "react";\n' + spFiles[fp];
      }

      // Auto-inject Link/useNavigate
      if (spFiles[fp].includes('<Link') && !spFiles[fp].includes("from 'react-router") && !spFiles[fp].includes('from "react-router')) {
        spFiles[fp] = 'import { Link, useNavigate } from "react-router-dom";\n' + spFiles[fp];
      }

      // Auto-inject BrowserRouter/Router import if <Router> or <BrowserRouter> is used without import
      if ((spFiles[fp].includes('<Router') || spFiles[fp].includes('<BrowserRouter')) &&
          !spFiles[fp].includes("from 'react-router") && !spFiles[fp].includes('from "react-router')) {
        spFiles[fp] = 'import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from "react-router-dom";\n' + spFiles[fp];
      }

      // Auto-inject useState/useEffect
      if ((spFiles[fp].includes('useState') || spFiles[fp].includes('useEffect')) && !spFiles[fp].match(/import.*\{[^}]*(useState|useEffect)[^}]*\}.*from.*react/)) {
        spFiles[fp] = spFiles[fp].replace(
          /import\s+React\s+from\s+['"]react['"];?/,
          'import React, { useState, useEffect } from "react";'
        );
      }

      // Add default values to destructured props
      spFiles[fp] = spFiles[fp].replace(
        /\(\{\s*(data|items|filter|search|columns|onSubmit|onClose|onEdit|onDelete|isOpen)([^}]*)\}\)/g,
        (match, _first, _rest, offset, full) => {
          // PARAM-VS-RETURNED-OBJECT-FIX: this is meant to add safe defaults
          // to a DESTRUCTURED FUNCTION PARAMETER (`function List({ items,
          // onSubmit }) {`), but the same `({ ... })` shape also matches an
          // arrow function's body wrapping a RETURNED/CONSTRUCTED object
          // literal — the standard `useMemo(() => ({ items, total }), deps)`
          // pattern. Confirmed live: that exact useMemo had its shorthand
          // `items,` property rewritten to `items = [],` — a default-value
          // initializer, which is invalid inside an object literal
          // ("Invalid shorthand property initializer"), a hard Babel parse
          // failure that took the whole CartContext out. A parameter list's
          // own `(` is never preceded by `=>`; a returned-object-literal's
          // `(` always is — skip the fix when that's what precedes it.
          const before = full.slice(Math.max(0, offset - 20), offset);
          if (/=>\s*$/.test(before)) return match;
          return match
            .replace(/\bdata\b(?!\s*[=:])/g, 'data = []')
            .replace(/\bitems\b(?!\s*[=:])/g, 'items = []')
            .replace(/\bfilter\b(?!\s*[=:])/g, 'filter = ""')
            .replace(/\bsearch\b(?!\s*[=:])/g, 'search = ""')
            .replace(/\bcolumns\b(?!\s*[=:])/g, 'columns = []')
            .replace(/\bonSubmit\b(?!\s*[=:])/g, 'onSubmit = () => {}')
            .replace(/\bonClose\b(?!\s*[=:])/g, 'onClose = () => {}')
            .replace(/\bonEdit\b(?!\s*[=:])/g, 'onEdit = () => {}')
            .replace(/\bonDelete\b(?!\s*[=:])/g, 'onDelete = () => {}')
            .replace(/\bisOpen\b(?!\s*[=:])/g, 'isOpen = false');
        }
      );

      // Replace named imports from external packages with stubs
      spFiles[fp] = spFiles[fp].replace(
        /import\s+\{([^}]*)\}\s+from\s+['"]([^'"./][^'"]*)['"];?/g,
        (match, names, pkg) => {
          if (isInstalledPackage(pkg)) return match;
          const iconNames = names.split(",").map(i => i.trim()).filter(Boolean);
          return iconNames.map(raw => {
            // Handle "X as Y" aliases — use alias (Y) as variable name
            const aliasMatch = raw.match(/^(\w+)\s+as\s+(\w+)$/);
            const name = aliasMatch ? aliasMatch[2] : raw;
            return "const " + name + " = ({className, children, size, ...p}) => <span className={className} {...p}>{children || '\u25cf'}</span>;";
          }).join("\n");
        }
      );

      // FIX 4 APPLIED — ensure React is imported if JSX stubs were added
      if (spFiles[fp].includes('= ({') && !spFiles[fp].match(/import React/)) {
        spFiles[fp] = 'import React from "react";\n' + spFiles[fp];
      }

      // FIX 5 APPLIED — unknown default imports render null instead of layout-breaking <div>
      spFiles[fp] = spFiles[fp].replace(
        /import\s+(\w+)\s+from\s+['"]([^'"./][^'"]*)['"];?/g,
        (match, name, pkg) => {
          if (isInstalledPackage(pkg)) return match;
          return "const " + name + " = () => null; // stubbed: " + pkg;
        }
      );

      // Remove Chart.register calls
      spFiles[fp] = spFiles[fp].replace(/\w+\.register\([^)]*\);?/g, "");

      // FIX: Replace framer-motion elements with plain HTML to avoid Sandpack bundling issues
      // motion.div → div, motion.section → section, etc.
      if (spFiles[fp].includes('motion.') || spFiles[fp].includes('<motion')) {
        spFiles[fp] = spFiles[fp]
          // DYNAMIC-MOTION-COMPONENT-FIX: a common, generic React/framer-motion
          // idiom — `const Component = motion[as] || motion.div;` then
          // `<Component>` — isn't a literal `<motion.xxx>` JSX tag, so the
          // tag-replacement regexes below never touch it, yet the import
          // strip a few lines down removes `motion` entirely regardless.
          // Confirmed live: this left `motion` referenced-but-undefined,
          // crashing with "Element type is invalid ... got undefined" the
          // moment the component rendered. `as` is already a plain HTML tag
          // *string* by the time it reaches this expression (that's the
          // whole point of the pattern), so `motion[as] || motion.div` and
          // `as || 'div'` are equivalent once motion is gone — same fix
          // shape as the literal-tag replacements below, just for the
          // variable form instead of a hardcoded one.
          .replace(/motion\[(\w+)\]\s*\|\|\s*motion\.(\w+)/g, "$1 || '$2'")
          .replace(/<motion\.(\w+)/g, '<$1')
          .replace(/<\/motion\.(\w+)>/g, '</$1>');
        // BALANCED-BRACE-FIX: these motion props almost always hold nested
        // object literals (`variants={{ hidden: {}, show: {...} }}` etc.) —
        // stripJsxPropsBalanced walks brace depth instead of a single-level
        // regex, so the whole value is removed instead of truncated mid-way.
        spFiles[fp] = stripJsxPropsBalanced(spFiles[fp], [
          'initial', 'animate', 'exit', 'transition', 'whileHover',
          'whileTap', 'whileInView', 'viewport', 'variants'
        ]);
        // USESCROLL-UNDEFINED-FIX: confirmed live — this used to blindly
        // delete the ENTIRE framer-motion import line, including hooks
        // like useScroll/useSpring/useAnimation/useMotionValue/useTransform/
        // useInView. Those are real JS function calls, not JSX tags — the
        // `<motion.x>` stripping above never touches them, so removing the
        // whole import left them referenced-but-undefined and crashed the
        // component the moment it rendered ("useScroll is not defined").
        // Only drop the two identifiers actually stripped from usage
        // (`motion`, `AnimatePresence`); keep every other named import as-is.
        spFiles[fp] = spFiles[fp].replace(
          /import\s+\{([^}]*)\}\s+from\s+['"]framer-motion['"];?/g,
          (match, names) => {
            const kept = names.split(',').map(n => n.trim()).filter(Boolean)
              .filter(n => !/^(motion|AnimatePresence)$/.test(n.split(/\s+as\s+/)[0].trim()));
            return kept.length ? `import { ${kept.join(', ')} } from 'framer-motion';` : '';
          }
        );
        spFiles[fp] = spFiles[fp].replace(/import\s+\w+\s+from\s+['"]framer-motion['"];?\s*/g, '');
        // Remove AnimatePresence wrapper (just render children)
        spFiles[fp] = spFiles[fp].replace(/<AnimatePresence[^>]*>/g, '').replace(/<\/AnimatePresence>/g, '');
      }

      // AUTO-FIX: <a href="/path"> → <Link to="/path"> for internal routes ONLY
      // CRITICAL: Do NOT touch external links (mailto:, http:, https:, tel:)
      if (spFiles[fp].includes('<a href="/') || spFiles[fp].includes("<a href='/")) {
        if (!spFiles[fp].includes('Link') && !spFiles[fp].includes('react-router')) {
          spFiles[fp] = 'import { Link, useNavigate } from "react-router-dom";\n' + spFiles[fp];
        }
        // Replace ONLY internal routes (starting with /) — match opening AND closing together
        spFiles[fp] = spFiles[fp]
          .replace(/<a\s+href="(\/[^"]*)"([^>]*)>([^<]*)<\/a>/g, '<Link to="$1"$2>$3</Link>')
          .replace(/<a\s+href='(\/[^']*)'([^>]*)>([^<]*)<\/a>/g, "<Link to='$1'$2>$3</Link>");
      }

      // AUTO-FIX: Remove markdown code blocks that AI sometimes generates inside code
      if (spFiles[fp].includes('```')) {
        spFiles[fp] = spFiles[fp]
          .replace(/```(jsx|js|javascript|tsx|typescript)?\n?/g, '')
          .replace(/```\n?/g, '');
      }

      // FINAL SAFETY: If <Router> is used but Router is not defined anywhere in the file, define it
      if (spFiles[fp].includes('<Router') && !spFiles[fp].match(/\b(const|let|var|import)\s+.*\bRouter\b/)) {
        // Router is used in JSX but never imported or declared
        if (spFiles[fp].includes('BrowserRouter') && !spFiles[fp].includes('as Router')) {
          // BrowserRouter is imported but not aliased
          spFiles[fp] = spFiles[fp].replace(
            /(import\s+\{[^}]*BrowserRouter[^}]*)\}\s+from\s+['"]react-router-dom['"]/,
            '$1, BrowserRouter as Router } from "react-router-dom"'
          );
          // If that didn't work (BrowserRouter already there), just add const
          if (!spFiles[fp].includes('as Router')) {
            spFiles[fp] = 'const Router = require("react-router-dom").BrowserRouter || function({children}){return children};\n' + spFiles[fp];
          }
        } else if (!spFiles[fp].includes('react-router-dom')) {
          spFiles[fp] = 'import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";\n' + spFiles[fp];
        }
      }
    }

    // FIX 3 APPLIED — single pass stub creator with deep search
    const snapshotKeys = [...Object.keys(spFiles)];
    for (const fp of snapshotKeys) {
      if (!fp.endsWith('.js')) continue;
      const code = spFiles[fp];
      const importRegex = /import\s+(?:(\w+)|\{([^}]+)\})\s+from\s+['"](\.\.\/[^'"]+|\.\/[^'"]+)['"];?/g;
      let m;
      while ((m = importRegex.exec(code)) !== null) {
        const defaultName = m[1];
        const namedImports = m[2];
        const importPath = m[3];
        const dir = fp.substring(0, fp.lastIndexOf('/'));
        const parts = (dir + '/' + importPath).split('/');
        const resolved = [];
        for (const p of parts) {
          if (p === '..') resolved.pop();
          else if (p !== '.' && p !== '') resolved.push(p);
        }
        const resolvedPath = '/' + resolved.join('/');
        const possiblePaths = [
          resolvedPath, resolvedPath + '.js', resolvedPath + '/index.js'
        ];
        const existingFile = possiblePaths.find(p => spFiles[p]);
        if (existingFile) {
          // File EXISTS but might not export the required functions
          // Check if named imports are actually exported by the file
          if (namedImports && spFiles[existingFile]) {
            const fileContent = spFiles[existingFile];
            const names = namedImports.split(',').map(n => n.trim()).filter(Boolean);
            const missingExports = [];
            for (const raw of names) {
              const aliasMatch = raw.match(/^(\w+)\s+as\s+(\w+)$/);
              const funcName = aliasMatch ? aliasMatch[1] : raw;
              // Check if function is exported in the file
              if (!fileContent.includes(`export function ${funcName}`) &&
                  !fileContent.includes(`export const ${funcName}`) &&
                  !fileContent.includes(`export async function ${funcName}`) &&
                  !fileContent.includes(`export { ${funcName}`) &&
                  !fileContent.includes(`${funcName}:`)) {
                missingExports.push(funcName);
              }
            }
            // Add missing exports as stubs to the existing file
            if (missingExports.length > 0) {
              const stubs = missingExports.map(name => {
                if (name.match(/^(get|fetch|load|find|search|list|create|update|delete)/i)) {
                  return `\nexport async function ${name}() { return []; }`;
                }
                // Same VALUE-VS-FUNCTION-STUB-FIX reasoning as above: a
                // lowercase missing export is usually data, and a truthy
                // function stub defeats every `|| []` fallback.
                if (/^[A-Z]/.test(name)) {
                  return `\nexport function ${name}() { return null; }`;
                }
                return `\nexport const ${name} = undefined;`;
              }).join('\n');
              spFiles[existingFile] = spFiles[existingFile] + '\n' + stubs + '\n';
            }
          }
          continue;
        }

        // Deep search — find by filename anywhere in the tree
        const baseName = resolvedPath.split('/').pop().toLowerCase();
        let bestMatch = null;

        // 1. Exact name match anywhere
        bestMatch = snapshotKeys.find(k => {
          const kBase = k.split('/').pop().replace('.js', '').toLowerCase();
          return kBase === baseName && k !== fp;
        });

        // 2. Name with Page/Component suffix
        if (!bestMatch) {
          bestMatch = snapshotKeys.find(k => {
            const kBase = k.split('/').pop().replace('.js', '').toLowerCase();
            return (kBase === baseName + 'page' || kBase === baseName + 'component' ||
                    kBase.replace('page', '') === baseName || kBase.replace('component', '') === baseName) && k !== fp;
          });
        }

        // 3. Partial match (starts with)
        if (!bestMatch) {
          bestMatch = snapshotKeys.find(k => {
            const kBase = k.split('/').pop().replace('.js', '').toLowerCase();
            return (kBase.startsWith(baseName) || baseName.startsWith(kBase)) && k !== fp && kBase.length > 2;
          });
        }

        if (bestMatch) {
          spFiles[resolvedPath + '.js'] =
            `export { default } from '${bestMatch.replace('.js', '')}';\n`;
        } else if (defaultName) {
          // For Layout/Navbar/Header stubs — generate nav with links from App routes
          const isNavComponent = /^(Layout|Navbar|Header|Nav|Sidebar|Navigation|TopBar|AppLayout|MainLayout|DashboardLayout)$/i.test(defaultName);
          if (isNavComponent) {
            // Extract routes from App.js to build navigation
            const appContent = spFiles['/App.js'] || Object.values(spFiles).find(c => c.includes('<Routes') && c.includes('<Route')) || '';
            const routeMatches = [...appContent.matchAll(/path=["']([^"']+)["']/g)];
            const navLinks = routeMatches
              .map(m => m[1])
              .filter(p => p !== '*')
              .slice(0, 12)
              .map(p => {
                const label = p === '/' ? 'Home' : p.replace(/^\//,'').replace(/[-/]/g,' ').replace(/\b\w/g, c => c.toUpperCase());
                return `<Link to="${p}" style={{display:'block',padding:'10px 16px',color:'#e2e8f0',textDecoration:'none',borderRadius:'6px',marginBottom:'2px'}}>${label}</Link>`;
              }).join('\n            ');
            spFiles[resolvedPath + '.js'] = `import React from "react";
import { Link } from "react-router-dom";
export default function ${defaultName}({ children, className, ...props }) {
  return (
    <div style={{display:'flex',minHeight:'100vh'}} className={className} {...props}>
      <aside style={{width:'220px',background:'#1e293b',padding:'16px 12px',display:'flex',flexDirection:'column'}}>
        <h2 style={{color:'#fff',fontSize:'16px',fontWeight:'bold',padding:'8px 16px',marginBottom:'16px'}}>Menu</h2>
        <nav style={{display:'flex',flexDirection:'column',gap:'2px'}}>
            ${navLinks || '<Link to="/" style={{display:"block",padding:"10px 16px",color:"#e2e8f0",textDecoration:"none"}}>Home</Link>'}
        </nav>
      </aside>
      <main style={{flex:1,overflow:'auto',padding:'24px',background:'#f9fafb'}}>{children}</main>
    </div>
  );
}
`;
          } else {
            spFiles[resolvedPath + '.js'] = `import React from "react";
export default function ${defaultName}({ children, className, ...props }) {
  return <div className={className} {...props}>{children || null}</div>;
}
`;
          }
        } else if (namedImports) {
          const names = namedImports.split(',').map(n => n.trim()).filter(Boolean);
          spFiles[resolvedPath + '.js'] =
            names.map(n => {
              // Handle aliased imports: "X as Y" -> export const Y
              const aliasMatch = n.match(/^(\w+)\s+as\s+(\w+)$/);
              const exportName = aliasMatch ? aliasMatch[2] : n;
              // If name looks like a getter/fetcher, return async stub with empty array
              if (exportName.match(/^(get|fetch|load|find|search|list|create|update|delete)/i)) {
                return `export const ${exportName} = async () => [];`;
              }
              // VALUE-VS-FUNCTION-STUB-FIX (real user report, job 41397b20):
              // every named export used to be stubbed as `() => null`. A
              // function is TRUTHY, so the extremely common defensive
              // pattern `(navLinks || []).filter(...)` kept the function
              // instead of falling back, and threw "filter is not a
              // function" — turning a missing module into a hard crash of
              // the whole app. Only a Capitalised name is reliably a
              // component; a lowercase one is usually DATA, and stubbing it
              // as undefined lets `|| []`, `?? {}` and `?.` all behave.
              if (/^[A-Z]/.test(exportName)) {
                return `export const ${exportName} = () => null;`;
              }
              return `export const ${exportName} = undefined;`;
            }).join('\n') + '\n';
        }
      }
    }

    // Check if App file exists
    const appFile = componentFiles.find(f => f.match(/\/App\.(js|jsx|ts|tsx)$/));

    // If no App.js at root, create one
    if (!spFiles['/App.js']) {
      const imports = [];
      const renders = [];

      if (appFile) {
        const appContent = spFiles[appFile] || '';
        const exportMatch = appContent.match(/export\s+default\s+function\s+(\w+)/);
        const componentName = exportMatch ? exportMatch[1] : 'App';
        // Keep the extension explicit — if appFile is already at the root
        // (e.g. /App.jsx), an extensionless "./App" import would be
        // ambiguous with the /App.js wrapper we're creating right here,
        // and bundler extension-resolution order (.js before .jsx) would
        // make it resolve back to itself instead of the real component.
        const importPath = appFile;
        const cssImports = cssFiles.map(f => `import "${f}";`).join('\n');

        // Check if the nested App already has BrowserRouter
        const hasBrowserRouter = appContent.includes('BrowserRouter') || appContent.includes('<Router');

        // Default-import binding name is local to this file, so we always
        // alias it to a fixed name — the real module's export is commonly
        // also named "App" (e.g. /App.jsx), which would otherwise collide
        // with the "export default function App()" declared below.
        if (hasBrowserRouter) {
          // NOTE: RealApp already renders its own <BrowserRouter> — this
          // comment keeps the literal word "BrowserRouter" in this file so
          // the downstream double-wrap safety pass (which greps for it)
          // doesn't redundantly nest a second Router around this one.
          spFiles['/App.js'] = `import React from "react";
${cssImports}
import RealApp from "${importPath}"; // BrowserRouter handled inside RealApp

export default function App() {
  return <RealApp />;
}
`;
        } else {
          // Wrap in BrowserRouter since nested App uses Link/Routes without it
          spFiles['/App.js'] = `import React from "react";
import { BrowserRouter } from "react-router-dom";
${cssImports}
import RealApp from "${importPath}";

export default function App() {
  return <BrowserRouter><RealApp /></BrowserRouter>;
}
`;
        }
      } else {
        // No App file — compose with BrowserRouter
        const pageFiles = [];
        for (const fp of componentFiles) {
          if (fp.match(/\/index\.(js|jsx|ts|tsx)$/)) continue;
          const fileName = fp.split('/').pop().replace(/\.(js|jsx|ts|tsx)$/, '');
          const fileContent = spFiles[fp] || '';
          const exportMatch = fileContent.match(/export\s+default\s+function\s+(\w+)/);
          const name = exportMatch ? exportMatch[1] : fileName;
          if (name[0] !== name[0].toUpperCase()) continue;
          if (name.includes('Layout') || name.includes('Sidebar') || name.includes('Header')) continue;
          const importPath = fp.replace(/\.(js|jsx|ts|tsx)$/, '');
          pageFiles.push({ name, importPath });
        }

        const allImports = pageFiles.map(p => `import ${p.name} from "${p.importPath}";`).join('\n');
        const routes = pageFiles.map((p, i) => {
          const path = i === 0 ? '/' : '/' + p.name.replace(/Page$/, '').toLowerCase();
          return `          <Route path="${path}" element={<${p.name} />} />`;
        }).join('\n');
        const navLinks = pageFiles.map((p, i) => {
          const path = i === 0 ? '/' : '/' + p.name.replace(/Page$/, '').toLowerCase();
          const label = p.name.replace(/Page$/, '');
          return `            <Link to="${path}" style={{display:'block',padding:'10px 16px',color:location.pathname==="${path}"?'#fff':'#9ca3af',background:location.pathname==="${path}"?'#374151':'transparent',borderRadius:'8px',textDecoration:'none',marginBottom:'4px'}}>${label}</Link>`;
        }).join('\n');

        spFiles['/App.js'] = `import React from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
${allImports}

function Layout({ children }) {
  const location = useLocation();
  return (
    <div style={{display:'flex',height:'100vh',fontFamily:'-apple-system,BlinkMacSystemFont,sans-serif'}}>
      <aside style={{width:'240px',background:'#111827',padding:'20px 12px',display:'flex',flexDirection:'column'}}>
        <h1 style={{color:'#fff',fontSize:'18px',fontWeight:'bold',padding:'0 16px 20px',borderBottom:'1px solid #374151',marginBottom:'16px'}}>\u26a1 App</h1>
        <nav>
${navLinks}
        </nav>
      </aside>
      <main style={{flex:1,overflow:'auto',padding:'24px',background:'#f9fafb'}}>{children}</main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
${routes}
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}
`;
      }
    }

    // Check if ROOT index.js exists (not nested like /types/index.ts)
    const hasRootIndex = Object.keys(spFiles).some(f => f === '/index.js' || f === '/index.jsx' || f === '/main.jsx' || f === '/main.js');
    if (!hasRootIndex) {
      spFiles['/index.js'] = `import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<React.StrictMode><App /></React.StrictMode>);
`;
    } else {
      // Fix existing index/main file if it uses ReactDOM without importing it
      const indexFile = ['/index.js', '/main.jsx', '/main.js', '/index.jsx'].find(f => spFiles[f]);
      if (indexFile && spFiles[indexFile]) {
        let idx = spFiles[indexFile];
        if (idx.includes('ReactDOM') && !idx.includes('import ReactDOM')) {
          idx = 'import ReactDOM from "react-dom/client";\n' + idx;
        }
        if (idx.includes('ReactDOM') && !idx.includes('import React')) {
          idx = 'import React from "react";\n' + idx;
        }
        // LEGACY-REACTDOM-RENDER-FIX: this platform generates Vite-style
        // projects, whose main.jsx template predates React 18 — the React
        // 17-era `import ReactDOM from 'react-dom'; ReactDOM.render(jsx,
        // container)` API. Sandpack's own dependencies resolve to React
        // 19, whose `react-dom` package no longer exports `.render` at
        // all (only `react-dom/client`'s `createRoot(...).render(...)`
        // does) — confirmed live: "_client.default.render is not a
        // function" the moment this entry file actually got executed
        // (previously masked entirely, since the SANDPACK-TEMPLATE-ENTRY-
        // FIX above is what makes this file run at all now — Sandpack's
        // own template default index.js already used the modern API, so
        // this generated-but-never-actually-run code's incompatibility
        // went unnoticed).
        if (/ReactDOM\.render\s*\(/.test(idx) && !/ReactDOM\.createRoot/.test(idx)) {
          idx = idx.replace(/from\s+(['"])react-dom\1/, 'from $1react-dom/client$1');
          idx = idx.replace(
            /ReactDOM\.render\(\s*([\s\S]*?),\s*document\.getElementById\((['"])([^'"]+)\2\)\s*\)/,
            (m, jsx, q, id) => `ReactDOM.createRoot(document.getElementById(${q}${id}${q})).render(\n  ${jsx.trim()}\n)`
          );
        }
        spFiles[indexFile] = idx;
      }
      // SANDPACK-TEMPLATE-ENTRY-FIX: `template="react"` (set below) is
      // Sandpack's own built-in CRA-style template, whose bootstrap ALWAYS
      // runs its fixed `/index.js` — that's baked into the template
      // definition, not something `files` can redirect. Confirmed live:
      // this platform's real entry is always `src/main.jsx` (normalized
      // to `/main.jsx`), never literally `/index.js`; the mock-injection
      // block above correctly prepends the `preview-backend-mock.js`
      // import into `/main.jsx`, but since spFiles never actually
      // contains `/index.js`, Sandpack silently falls back to ITS OWN
      // default `/index.js` (plain `import App from './App'; ReactDOM...`)
      // instead of ours — `/main.jsx` (mock import included) is simply
      // never imported by anything that runs, so `window.fetch` is never
      // overridden. The app still renders correctly (App/Layout/pages
      // resolve fine on their own), which is exactly why this went
      // unnoticed — but EVERY `/api/*` call then falls through to the
      // real native fetch, hitting the Sandpack iframe's own hosting
      // origin, which has no such route and returns its generic sandbox
      // HTML shell: confirmed live via network trace, a signup POST to
      // `/api/auth/register` landed on `2-19-8-sandpack.codesandbox.io`
      // and got back `<!doctype html>...<title>Sandbox - CodeSandbox`,
      // which `res.json()` then fails on — "Unexpected token '<' ... is
      // not valid JSON", on every single signup/login attempt. Forcing a
      // real `/index.js` that re-imports the actual entry closes the gap:
      // Sandpack's template now runs OUR file, which pulls in the mock
      // before anything else.
      if (indexFile && indexFile !== '/index.js') {
        spFiles['/index.js'] = `import './${indexFile.slice(1)}';\n`;
      }
    }

    // Add default styles.css if no CSS exists
    if (cssFiles.length === 0 && !spFiles['/styles.css']) {
      spFiles['/styles.css'] = DEFAULT_CSS;
      if (spFiles['/App.js'] && !spFiles['/App.js'].includes('styles.css')) {
        spFiles['/App.js'] = `import "./styles.css";\n` + spFiles['/App.js'];
      }
    }

    // PERMANENT FIX: Split merged multi-component files + remove duplicates
    for (const fp of Object.keys(spFiles)) {
      if (!fp.endsWith('.js') && !fp.endsWith('.jsx')) continue;
      let content = spFiles[fp];

      // Case 1: File has comment markers like "// src/components/X.jsx" indicating merged files
      // Split at these markers and keep only the FIRST component
      const mergeMarkerRegex = /\n\/\/\s*(src\/|components\/|pages\/|\.\/)[^\n]*\.(jsx?|tsx?)\s*\n/;
      const mergeMatch = content.match(mergeMarkerRegex);
      if (mergeMatch && mergeMatch.index > 50) {
        content = content.substring(0, mergeMatch.index).trim();
      }

      // Case 2: Multiple "import React" declarations — keep only first occurrence
      const reactImportRegex = /import React[^;]*;/g;
      const reactMatches = [...content.matchAll(reactImportRegex)];
      if (reactMatches.length > 1) {
        // Remove all but the first import React
        for (let i = reactMatches.length - 1; i >= 1; i--) {
          const match = reactMatches[i];
          content = content.substring(0, match.index) + content.substring(match.index + match[0].length);
        }
      }

      // Case 3: Multiple "export default" — keep only first component
      const exportMatches = [...content.matchAll(/export default /g)];
      if (exportMatches.length > 1) {
        content = content.substring(0, exportMatches[1].index).trim();
      }

      // Case 4: Remove any remaining markdown artifacts
      content = content
        .replace(/```(jsx|js|javascript|tsx|typescript)?\n?/g, '')
        .replace(/```\n?/g, '')
        .replace(/^#+ .+$/gm, '')  // Remove markdown headings
        .trim();

      // Case 5: Remove duplicate imports of same module
      const importLines = content.match(/^import .+ from ['"][^'"]+['"];?$/gm) || [];
      const seenImports = new Set();
      for (const imp of importLines) {
        const moduleMatch = imp.match(/from ['"]([^'"]+)['"]/);
        if (moduleMatch) {
          const key = moduleMatch[1];
          if (seenImports.has(key)) {
            content = content.replace(imp + '\n', '');
          } else {
            seenImports.add(key);
          }
        }
      }

      spFiles[fp] = content;
    }

    // Strip TypeScript syntax from .js files + fix Vite-specific syntax
    const allFilePaths = Object.keys(spFiles);
    for (const fp of allFilePaths) {
      if (!fp.endsWith('.js')) continue;

      // Replace ALL import.meta.* with safe fallbacks — Sandpack's Babel
      // transpiler runs in non-module (CommonJS) mode and throws
      // "Cannot use 'import.meta' outside a module" on any import.meta usage.
      spFiles[fp] = spFiles[fp]
        .replace(/import\.meta\.env\.\w+/g, '""')   // import.meta.env.VAR → ""
        .replace(/import\.meta\.hot\b[^;\n]*/g, 'null') // import.meta.hot → null
        .replace(/import\.meta\.url\b/g, '""')          // import.meta.url → ""
        .replace(/import\.meta\.glob\([^)]*\)/g, '{}')  // import.meta.glob(...) → {}
        .replace(/import\.meta\b/g, '({})');             // any remaining import.meta → ({})

      // Strip TypeScript syntax + fix broken patterns (handled by codeSanitizer)
      spFiles[fp] = spFiles[fp]
        .replace(/\w+\.defaultProps\s*=\s*\{[^}]*\};?/gs, '')
        .replace(/\w+\.propTypes\s*=\s*\{[^}]*\};?/gs, '');

      // Fix broken imports by fuzzy matching
      spFiles[fp] = spFiles[fp].replace(
        /import\s+(\w+)\s+from\s+['"](\.[./ ]*[^'"]+)['"];?/g,
        (match, name, importPath) => {
          const dir = fp.substring(0, fp.lastIndexOf('/'));
          const parts = (dir + '/' + importPath).split('/');
          const resolved = [];
          for (const p of parts) {
            if (p === '..') resolved.pop();
            else if (p !== '.' && p !== '') resolved.push(p);
          }
          const resolvedPath = '/' + resolved.join('/');
          if (spFiles[resolvedPath] || spFiles[resolvedPath + '.js']) return match;
          const targetName = resolvedPath.split('/').pop().toLowerCase();
          const targetDir = resolvedPath.substring(0, resolvedPath.lastIndexOf('/'));
          let bestMatch = null;
          for (const existing of allFilePaths) {
            const existingName = existing.split('/').pop().replace('.js', '').toLowerCase();
            const existingDir = existing.substring(0, existing.lastIndexOf('/'));
            if (existingDir === targetDir && (
              existingName.startsWith(targetName) ||
              targetName.startsWith(existingName) ||
              existingName.includes(targetName) ||
              targetName.includes(existingName)
            )) {
              bestMatch = existing;
              break;
            }
          }
          if (bestMatch) {
            const fromDir = fp.substring(0, fp.lastIndexOf('/'));
            let relPath = bestMatch.replace('.js', '');
            if (bestMatch.startsWith(fromDir + '/')) {
              relPath = './' + bestMatch.substring(fromDir.length + 1).replace('.js', '');
            } else {
              const fromParts = fromDir.split('/').filter(Boolean);
              const toParts = relPath.split('/').filter(Boolean);
              let common = 0;
              while (common < fromParts.length && common < toParts.length && fromParts[common] === toParts[common]) common++;
              const ups = fromParts.length - common;
              relPath = (ups > 0 ? '../'.repeat(ups) : './') + toParts.slice(common).join('/');
            }
            return `import ${name} from '${relPath}';`;
          }
          return match;
        }
      );
    }

    // ═══ FINAL STEP: Run AI Code Sanitizer + Babel AST Validation ═══
    const installedPackages = [
      'react', 'react-dom',
      ...Object.keys(SANDPACK_DEPS),
    ];
    try {
      const sanitized = sanitizeAllFiles(spFiles, installedPackages);
      Object.keys(spFiles).forEach(k => delete spFiles[k]);
      Object.assign(spFiles, sanitized);
    } catch (e) {
      // A throw here happens during render, before PreviewErrorBoundary can catch
      // it (that boundary only wraps SandpackProvider below) — swallow and fall
      // back to the unsanitized files rather than blanking the whole page.
      console.warn('[LivePreview] sanitizeAllFiles failed, using unsanitized files:', e.message);
    }

    // ═══ REPLIT-STYLE: Babel AST validation — catch ALL syntax errors ═══
    let babelErrorMessages = [];
    let errorFileCount = 0;
    let totalFileCount = Object.keys(spFiles).filter(f => f.endsWith('.js')).length;
    try {
      const { validFiles, errors, hasErrors } = validateAllFiles(spFiles);
      if (hasErrors) {
        babelErrorMessages = errors.map(e => `${e.path}:${e.line} ${e.error}`);
        // Count unique files with errors
        const errorFiles = new Set(errors.map(e => e.path));
        errorFileCount = errorFiles.size;
        console.warn('[LivePreview] Babel found errors:', babelErrorMessages);
      }
      // Replace spFiles with validated/fixed versions
      Object.keys(spFiles).forEach(k => delete spFiles[k]);
      Object.assign(spFiles, validFiles);
    } catch (e) {
      console.warn('[LivePreview] Babel validation failed, using sanitized files:', e.message);
    }

    // ═══ SMART PREVIEW GATE: Show preview immediately with stubs ═══
    // Silent auto-fix in background — no blocking, no UI
    if (babelErrorMessages.length > 0 && projectId && !autoFixCalled.current && fixAttempt < MAX_AUTO_FIX) {
      autoFixCalled.current = true;
      setTimeout(() => triggerAutoFix(babelErrorMessages.join('\n')), 3000);
    }

    // Save as last working state
    lastWorkingFiles.current = { ...spFiles };

    // ═══ SAFETY PASS: Validate files won't crash Babel ═══
    // Remove files that have obvious fatal syntax issues

    // SAFETY: Ensure BrowserRouter exists at root level if any file uses Link/useNavigate
    const anyFileUsesRouter = Object.values(spFiles).some(usesReactRouter);
    if (anyFileUsesRouter && spFiles['/App.js'] && !spFiles['/App.js'].includes('BrowserRouter') && !spFiles['/App.js'].includes('<Router')) {
      let app = spFiles['/App.js'];
      // Add BrowserRouter import
      const hasRouterDomImport = app.includes('react-router-dom');
      if (hasRouterDomImport) {
        app = app.replace(
          /import\s*\{([^}]*)\}\s*from\s*['"]react-router-dom['"]/,
          (match, imports) => {
            if (imports.includes('BrowserRouter')) return match;
            return `import { BrowserRouter, ${imports.trim()} } from "react-router-dom"`;
          }
        );
      } else {
        app = 'import { BrowserRouter, Routes, Route, Link } from "react-router-dom";\n' + app;
      }
      // Wrap the FIRST return's root element in BrowserRouter
      // Find the return statement and wrap its content
      const returnMatch = app.match(/return\s*\(\s*\n(\s*)</);
      if (returnMatch) {
        const indent = returnMatch[1];
        // Find matching closing paren for this return
        const returnStart = app.indexOf(returnMatch[0]);
        const jsxStart = app.indexOf('<', returnStart);
        // Find the ); that closes this return (track parens)
        let depth = 1;
        let i = app.indexOf('(', returnStart) + 1;
        while (i < app.length && depth > 0) {
          if (app[i] === '(') depth++;
          if (app[i] === ')') depth--;
          i++;
        }
        const returnEnd = i - 1; // position of closing )
        // Extract JSX content between return( and )
        const jsxContent = app.substring(jsxStart, returnEnd).trim();
        // Only wrap if not already wrapped
        if (!jsxContent.startsWith('<BrowserRouter')) {
          const wrapped = `${indent}<BrowserRouter>\n${indent}  ${jsxContent}\n${indent}</BrowserRouter>`;
          app = app.substring(0, jsxStart) + wrapped + '\n' + indent.slice(2) + app.substring(returnEnd);
        }
      }
      spFiles['/App.js'] = app;
    }

    for (const fp of Object.keys(spFiles)) {
      if (!fp.endsWith('.js')) continue;
      const content = spFiles[fp];

      // ABSOLUTE SAFETY: Fix Router not defined — the #1 most common error
      if (content.includes('<Router') && !/\b(import|const|let|var|function)\b[^\n]*\bRouter\b/.test(content)) {
        spFiles[fp] = 'import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from "react-router-dom";\n' + content;
      }
      if (!content || content.trim().length < 3) {
        // Empty or near-empty file — provide stub
        spFiles[fp] = 'export default function() { return null; }\n';
        continue;
      }
      // BOOTSTRAP-FRAGMENT-FIX: isFatalSyntax's "just a fragment" check
      // flags any file containing none of function/const/export/class as
      // unparseable — correct for a page/component file, wrong for the
      // root entry script, which is LEGITIMATELY nothing but imports plus
      // a bare `ReactDOM.render(...)`/`import './main.js'` statement.
      // Confirmed live: this stubbed BOTH `/main.js` (the real app entry —
      // "import React...; ReactDOM.render(<App/>, ...)", no function/
      // const/export/class anywhere in it) and the `/index.js` redirect
      // just above into a nonsense "has syntax errors" placeholder
      // COMPONENT — which Sandpack's template then runs as the entry
      // instead of actually mounting the app, rendering nothing at all
      // (blank preview). These two files are deterministically managed
      // right above, not LLM output that could be broken — skip the
      // generic safety net entirely.
      const isBootstrapEntry = fp === '/index.js' || fp === '/main.js' || fp === '/index.jsx' || fp === '/main.jsx';
      // Check for fatal patterns that crash Babel's worker
      if (!isBootstrapEntry && isFatalSyntax(content)) {
        // Try to fix before giving up
        const fixed = tryFixJsxSyntax(content);
        if (fixed && !isFatalSyntax(fixed)) {
          spFiles[fp] = fixed;
        } else {
          const fnName = fp.split('/').pop().replace('.js', '').replace(/[^a-zA-Z]/g, '') || 'Component';
          spFiles[fp] = `import React from "react";\nexport default function ${fnName}() {\n  return <div style={{padding:24,color:"#f59e0b"}}>⚠️ ${fnName} has syntax errors</div>;\n}\n`;
        }
      }
    }

    return spFiles;
  }, [filesKey, isFullstackProject, realPreview.status]); // FILESKEY-FIX APPLIED

  useEffect(() => {
    setPreviewLoading(true);
    // Show preview after Sandpack ready event OR a forced timeout.
    // SANDPACK-TIMEOUT-TOO-SHORT-FOR-REAL-PREVIEW-FIX: this was a flat 8s
    // for every project, written with only the Sandpack (client-side
    // bundler) path in mind — it settles well within 8s. The real
    // dev-server path (isFullstackProject) can legitimately take much
    // longer on a cold start (npm install cache miss + vite boot — bounded
    // internally at up to INSTALL_TIMEOUT_SEC=180s + _START_TIMEOUT_SEC=90s,
    // see backend/core/build_verifier.py / sandbox/dynamic_frontend_preview.py),
    // so the old flat 8s force-hid the loading overlay long before the
    // real content was ready. This is only a backstop now (the primary
    // signal is realPreview.status reaching 'running'/'failed', which
    // already clears previewLoading via onLoad/SandpackReadyListener) —
    // Sandpack's own 8s behavior is unchanged.
    const ms = isFullstackProject ? 120000 : 8000;
    const forceT = setTimeout(() => setPreviewLoading(false), ms);
    return () => { clearTimeout(forceT); };
  }, [filesKey, isFullstackProject]);

  // ORDER-FIX APPLIED — framework detection FIRST, null check LAST
  const framework = detectFramework(files);

  if (framework === 'html') {
    const htmlContent = buildHtmlPreview(files);
    return (
      <div style={styles.container}>
        <div style={styles.toolbar}>
          <span style={styles.badge}>HTML / Static</span>
        </div>
        <div style={{ ...styles.previewWrapper, justifyContent: vp.width ? 'center' : 'stretch' }}>
          <div style={vp.width ? { ...styles.deviceFrame, width: vp.width, borderRadius: viewport === 'mobile' ? 20 : 16 } : { width: '100%' }}>
            <iframe
              srcDoc={htmlContent}
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              sandbox="allow-scripts allow-same-origin"
              title="preview"
            />
          </div>
        </div>
      </div>
    );
  }

  if (framework === 'python' || framework === 'node' || framework === 'angular' || framework === 'java' || framework === 'dotnet') {
    const labels = { python: 'Python', node: 'Node.js', angular: 'Angular', java: 'Java', dotnet: '.NET' };
    const runHints = {
      angular: 'Angular requires ng serve to preview. Download ZIP and run locally.',
      java: 'Java projects require a local build (Maven/Gradle) to preview. Download ZIP and run locally.',
      dotnet: '.NET projects require a local build (dotnet run) to preview. Download ZIP and run locally.',
    };
    return (
      <div style={styles.empty}>
        <span style={{ fontSize: 48, opacity: 0.3 }}>⚙️</span>
        <p style={{ color: '#94a3b8', marginTop: 12 }}>
          {labels[framework]} project detected.
        </p>
        <p style={{ color: '#64748b', fontSize: 13, marginTop: 6 }}>
          {runHints[framework] || 'Live preview is only available for frontend projects.'}
        </p>
        <p style={{ color: '#475569', fontSize: 12, marginTop: 12 }}>
          📦 Use "Download ZIP" to get the full project code.
        </p>
      </div>
    );
  }

  // SKIP-WASTED-SANDPACK-PREP-REGRESSION-FIX: sandpackConfig is now
  // DELIBERATELY null while the real-dev-server iframe is the one
  // actually rendering (isFullstackProject && status !== 'failed' — see
  // that useMemo's own comment) — this early return used to fire on that
  // null regardless of WHY it was null, intercepting before the real
  // iframe's own render branch further down ever got a chance to run and
  // showing "No previewable frontend files found" over a project that
  // was actually rendering fine. Confirmed live: this is exactly what
  // broke right after the skip-wasted-computation change above — same
  // commit, same turn, caught before it shipped.
  if (!sandpackConfig && !(isFullstackProject && realPreview.status !== 'failed')) {
    return (
      <div style={styles.empty}>
        <span style={{ fontSize: 48, opacity: 0.3 }}>📂</span>
        <p style={{ color: '#94a3b8', marginTop: 12 }}>No previewable frontend files found</p>
        <p style={{ color: '#475569', fontSize: 12, marginTop: 12 }}>
          📦 Use "Download ZIP" to get the full project code.
        </p>
      </div>
    );
  }

  const sandpackTemplate = SANDPACK_TEMPLATE_MAP[framework] || 'react';

  return (
    <div style={styles.container}>
      {/* SMART PREVIEW GATE: Block preview when too many errors, show fixing UI */}
      {previewLoading && (
        <div style={styles.previewSkeletonOverlay} className="skeleton-fade-in">
          <PreviewSkeleton />
        </div>
      )}
      {/* Inline auto-fix indicator — small pill at top-right of preview */}
      {(isFixing || (previewLoading && fixAttempt > 0)) && (
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(15,23,42,0.9)', borderRadius: 20, padding: '5px 12px', fontSize: 11, color: '#94a3b8', backdropFilter: 'blur(4px)' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', animation: 'pulse 1.2s infinite' }} />
          <span>Auto-fixing{fixAttempt > 0 ? ` (${fixAttempt}/3)` : '...'}</span>
          <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }`}</style>
        </div>
      )}
      <PreviewErrorBoundary>
      {isFullstackProject && realPreview.status === 'running' ? (
        <>
          <div style={styles.toolbar}>
            <RealPreviewNavigator
              viewport={viewport}
              onViewport={handleViewport}
              url={realPreview.url}
              onRefresh={() => { startRealPreview(); setRealPreviewReloadTick(t => t + 1); }}
            />
            {onElementEditRequest && (
              <button
                onClick={toggleEditMode}
                title={editMode ? 'Exit edit mode' : 'Click an element in the preview, then describe a change'}
                style={{
                  marginLeft: 8, padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: editMode ? '1px solid #6366f1' : '1px solid var(--border-color, #334155)',
                  background: editMode ? '#6366f1' : 'transparent',
                  color: editMode ? '#fff' : 'var(--text-secondary, #94a3b8)',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {editMode ? '✓ Editing' : '✎ Edit'}
              </button>
            )}
          </div>
          <div style={{ ...styles.previewWrapper, justifyContent: vp.width ? 'center' : 'stretch', opacity: previewLoading ? 0.2 : 1, transition: 'opacity 220ms ease', position: 'relative' }}>
            <div style={vp.width ? { ...styles.deviceFrame, width: vp.width, borderRadius: viewport === 'mobile' ? 20 : 16 } : { width: '100%' }}>
              {previewOriginError ? (
                <div style={{
                  minHeight: 550, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 12, padding: 32,
                  background: 'var(--bg-card, #1e293b)', borderRadius: 12, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #e2e8f0)' }}>
                    The preview host isn&apos;t reachable
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary, #94a3b8)', maxWidth: 560, lineHeight: 1.6 }}>
                    Your app built fine — this is about where the preview is served from.
                    It runs on{' '}
                    <code style={{ color: '#a5b4fc' }}>{previewOriginError.replace(/^https?:\/\//, '')}</code>,
                    a separate origin, so the generated app can never read this page&apos;s
                    login token. The browser could not reach that host at all.
                  </div>
                  <div style={{
                    fontSize: 12, color: 'var(--text-secondary, #94a3b8)', textAlign: 'left',
                    background: 'var(--bg-input, #0f172a)', borderRadius: 8, padding: '10px 14px',
                    maxWidth: 560, lineHeight: 1.8,
                  }}>
                    Three things have to line up for it, and whoever runs this deployment
                    sets all three:
                    <br />1. a DNS record for that hostname pointing at this same server
                    <br />2. a TLS certificate that covers it
                    <br />3. an nginx server block for it
                  </div>
                  <button
                    onClick={() => { setPreviewOriginError(null); setRealPreviewReloadTick(t => t + 1); startRealPreview(); }}
                    style={{
                      marginTop: 4, padding: '8px 16px', borderRadius: 8, border: 'none',
                      background: '#6366f1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Try again
                  </button>
                </div>
              ) : (
              <iframe
                ref={realIframeRef}
                key={`${realPreview.url}-${realPreviewReloadTick}`}
                src={realPreview.url}
                title="Live Preview"
                style={{ width: '100%', height: '100%', minHeight: 550, border: 'none', cursor: editMode ? 'crosshair' : 'default' }}
                // PREVIEW-SUBDOMAIN-ORIGIN-ISOLATION-FIX: allow-same-origin
                // is safe here specifically BECAUSE realPreview.url points
                // at the dedicated preview.* subdomain, a genuinely
                // different origin from this page — see nginx.conf.
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                onLoad={() => {
                  setPreviewLoading(false);
                  if (editMode) realIframeRef.current?.contentWindow?.postMessage({ __dynpreviewEditMode: true }, '*');
                }}
              />
              )}
              {previewBuildError && !previewOriginError && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 15,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 14, padding: 32,
                  background: 'var(--bg-card, #1e293b)', borderRadius: 12, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #e2e8f0)' }}>
                    This page didn&apos;t compile
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary, #94a3b8)', maxWidth: 520, lineHeight: 1.6 }}>
                    One file has a mistake in it, so the app can&apos;t start. The rest of
                    your project is untouched — fixing that one file brings the preview back.
                  </div>
                  <details style={{ maxWidth: 560, width: '100%', textAlign: 'left' }}>
                    <summary style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)', cursor: 'pointer' }}>
                      What went wrong
                    </summary>
                    <div style={{
                      fontSize: 12, color: 'var(--text-secondary, #94a3b8)', marginTop: 8,
                      background: 'var(--bg-input, #0f172a)', borderRadius: 8, padding: '10px 14px',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 180, overflow: 'auto',
                    }}>
                      {previewBuildError}
                    </div>
                  </details>
                  {projectId && !shareToken && (
                    <button
                      onClick={() => triggerAutoFix(previewBuildError)}
                      style={{
                        marginTop: 4, padding: '8px 16px', borderRadius: 8, border: 'none',
                        background: '#6366f1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      Fix this error
                    </button>
                  )}
                </div>
              )}
            </div>
            {pendingElementEdit && (
              <div
                style={{
                  position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                  width: 'min(420px, 90%)', background: 'var(--bg-card, #1e293b)',
                  border: '1px solid #6366f1', borderRadius: 12, padding: 14, zIndex: 20,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--text-secondary, #94a3b8)', marginBottom: 8 }}>
                  Editing: <code style={{ color: '#a5b4fc' }}>{`<${pendingElementEdit.tag}>`}</code>
                  {pendingElementEdit.text ? ` "${pendingElementEdit.text.slice(0, 40)}${pendingElementEdit.text.length > 40 ? '…' : ''}"` : ''}
                </div>
                <textarea
                  autoFocus
                  value={elementEditText}
                  onChange={(e) => setElementEditText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitElementEdit(); } }}
                  placeholder="Describe the change, e.g. 'make this blue and bigger'"
                  rows={2}
                  style={{
                    width: '100%', resize: 'none', borderRadius: 8, border: '1px solid var(--border-color, #334155)',
                    background: 'var(--bg-input, #0f172a)', color: 'var(--text-primary, #e2e8f0)',
                    padding: 8, fontSize: 13, fontFamily: 'inherit',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => setPendingElementEdit(null)}
                    style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, border: '1px solid var(--border-color, #334155)', background: 'transparent', color: 'var(--text-secondary, #94a3b8)', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitElementEdit}
                    disabled={!elementEditText.trim()}
                    style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', background: elementEditText.trim() ? '#6366f1' : '#334155', color: '#fff', cursor: elementEditText.trim() ? 'pointer' : 'not-allowed' }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : isFullstackProject && (realPreview.status === 'starting' || realPreview.status === 'idle') ? (
        <>
          <div style={styles.toolbar}>
            <RealPreviewNavigator
              viewport={viewport}
              onViewport={handleViewport}
              url={realPreview.url}
              onRefresh={() => { startRealPreview(); setRealPreviewReloadTick(t => t + 1); }}
            />
          </div>
          <div style={{ ...styles.previewWrapper, justifyContent: vp.width ? 'center' : 'stretch' }}>
            {/* BLANK-STARTING-PANEL-FIX: this branch used to render a bare
                empty div here, relying entirely on the separate overlay
                above (gated by `previewLoading`) to visually cover it. A
                real preview's cold start (npm install + vite boot) can run
                well past that overlay's forced-hide timeout, exposing this
                div's own emptiness underneath. Giving the branch its own
                loading content means it's never blank regardless of the
                overlay's timing. */}
            <div style={vp.width ? { ...styles.deviceFrame, width: vp.width, borderRadius: viewport === 'mobile' ? 20 : 16 } : { width: '100%', minHeight: 550 }}>
              <PreviewSkeleton />
              <div style={{ textAlign: 'center', color: '#64748b', fontSize: 12, padding: '10px 0' }}>
                Starting live preview… first load can take a minute.
              </div>
            </div>
          </div>
        </>
      ) : (
      <SandpackProvider
        // STALE-IFRAME-FIX: Sandpack's bundler runs in its own iframe +
        // web worker (sandbox.*.js, babel-transpiler.*.worker.js), a
        // SEPARATE browser context from this app. Confirmed live: even
        // with a verified-fresh `files` prop (correct filesKey, correct
        // server data, no HTTP caching anywhere), the running Sandpack
        // instance kept transpiling stale content — its own internal
        // worker/iframe state doesn't reliably get invalidated just
        // because the `files` prop object changed, and "clear site data"
        // on THIS app's origin doesn't touch Sandpack's separate
        // iframe/worker context either. A `key` tied to real file content
        // forces React to fully unmount and remount SandpackProvider
        // (tearing down and recreating the iframe/worker from scratch)
        // whenever content genuinely changes, instead of relying on
        // Sandpack's own internal diffing to catch it.
        key={filesKey}
        template={sandpackTemplate}
        files={sandpackConfig}
        theme={sandpackTheme}
        options={{
          showNavigator: false,
          showTabs: false,
          bundlerTimeOut: 30000,
          logLevel: 0,
          externalResources: [
            "https://cdn.tailwindcss.com",
            "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css",
            "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css",
            "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap",
          ],
        }}
        // CUSTOM-ENTRY-FIX: `template="react"` bundles a FIXED built-in
        // environment whose own default entry file it always runs,
        // regardless of what `files` provides at that same path —
        // confirmed live: the rendered preview's `document.title` stayed
        // "Sandbox - CodeSandbox" (Sandpack's own default HTML) and
        // `window.fetch` stayed native even after both the custom
        // `/index.js` entry file AND a custom `/public/index.html` with
        // an inline `<script>` were provided — neither ever actually
        // ran. `customSetup.entry` (passed straight through to
        // sandpack-client's bundler config as `entry`) is the real,
        // supported override for this — `/index.js` is unconditionally
        // guaranteed to exist by this point (see the hasRootIndex block
        // above: it's either created directly, or redirected to import
        // whatever the real entry file is), so the mock-import chain
        // injected into it now actually executes before the app mounts.
        customSetup={{ dependencies: SANDPACK_DEPS, entry: '/index.js' }}
      >
        <SandpackErrorListener onRuntimeError={onRuntimeError} />
        <SandpackReadyListener onReady={() => setPreviewLoading(false)} />
        <div style={styles.toolbar}>
          <CustomNavigator viewport={viewport} onViewport={handleViewport} />
        </div>
        <div style={{ ...styles.previewWrapper, justifyContent: vp.width ? 'center' : 'stretch', opacity: previewLoading ? 0.2 : 1, transition: 'opacity 220ms ease' }}>
          <div style={vp.width ? { ...styles.deviceFrame, width: vp.width, borderRadius: viewport === 'mobile' ? 20 : 16 } : { width: '100%' }}>
            <SandpackLayout style={{ border: 'none', flexDirection: 'column' }}>
              <SandpackPreview
                style={{ height: '100%', minHeight: 550 }}
                showNavigator={false}
                showRefreshButton={false}
                showOpenInCodeSandbox={true}
              />
            </SandpackLayout>
          </div>
        </div>
      </SandpackProvider>
      )}
      </PreviewErrorBoundary>
    </div>
  );
}

/**
 * Detect fatal syntax patterns that will crash Babel's web worker.
 * These patterns cause "Cannot assign to read only property 'message'" errors.
 */
function isFatalSyntax(code) {
  // Line starts with a comma (broken defaultProps artifact)
  if (/^\s*,/m.test(code) && code.split('\n').filter(l => /^\s*,$/.test(l)).length > 0) {
    return true;
  }
  // Unmatched braces (very rough check)
  const opens = (code.match(/\{/g) || []).length;
  const closes = (code.match(/\}/g) || []).length;
  if (Math.abs(opens - closes) > 3) return true;
  // Unmatched parentheses
  const openParens = (code.match(/\(/g) || []).length;
  const closeParens = (code.match(/\)/g) || []).length;
  if (Math.abs(openParens - closeParens) > 3) return true;
  // Unmatched JSX angle brackets (< vs />  and </)
  const jsxOpens = (code.match(/<[A-Z][a-zA-Z]*/g) || []).length;
  const jsxCloses = (code.match(/<\/[A-Z][a-zA-Z]*/g) || []).length;
  const selfClosing = (code.match(/\/>/g) || []).length;
  if (jsxOpens > jsxCloses + selfClosing + 3) return true;
  if (jsxCloses > jsxOpens + 3) return true;
  // Empty element={} attribute (Babel crash)
  if (/element=\{\s*\}/.test(code)) return true;
  // Double closing tag like </BrowserRouter>>
  if (/<\/\w+>>/.test(code)) return true;
  // File is just a fragment (no function/const/export)
  if (!code.includes('function') && !code.includes('const ') && !code.includes('export ') && !code.includes('class ')) {
    return true;
  }
  // Contains raw markdown code fence
  if (code.includes('```')) return true;
  // JSX colon attributes (type: "text" instead of type="text")
  // Only flag if colon attr is OUTSIDE of style={{ }} blocks
  // Check: tag has colon attr pattern BUT the match is NOT inside {{ }}
  const codeWithoutStyles = code.replace(/\{\{[^}]*\}\}/g, ''); // strip inline styles
  if (/<[a-zA-Z][\w]*\s+\w+:\s*["'{]/.test(codeWithoutStyles)) return true;
  // FALSE-POSITIVE-FIX: a "standalone empty colon attr" check used to live
  // here — `/^\s+\w+:\s*['"]['"]\s*$/m` gated only by "does the FILE
  // contain value=/onChange=/className=/placeholder= ANYWHERE" (not
  // whether the flagged line itself is actually inside a JSX tag).
  // Confirmed live: a completely ordinary form-state initializer —
  // `const initialForm = { ..., message: '' }` — matches that pattern,
  // and since any real form file naturally has onChange=/value= elsewhere,
  // this flagged totally valid code as fatal. Once flagged,
  // tryFixJsxSyntax's (also unscoped) fix pass then rewrote EVERY
  // `word: '...'` line in the whole file, corrupting unrelated plain
  // object properties (e.g. `address: '123 Main St'` inside a data
  // array) into invalid `address="123 Main St"` — the exact reported
  // crash. The tag-scoped fix a few lines below in tryFixJsxSyntax
  // already catches genuine colon-attr mistakes correctly (matched
  // against actual `<Tag ...>` text, not arbitrary file-wide lines), so
  // this broader, false-positive-prone check isn't needed.
  return false;
}

// Removes JSX props like `propName={...}` where the value itself contains
// nested braces — e.g. `variants={{ hidden: {}, show: { transition: {...} } }}`,
// a near-universal framer-motion pattern. A naive `propName=\{[^}]*\}` regex
// stops at the FIRST inner `}` (part of `hidden: {}`), truncating the prop
// mid-value and leaving the remainder (`, show: {...} }}`) dangling in the
// output as broken syntax. Confirmed live against Reveal.jsx's Stagger/
// StaggerItem — walks brace depth instead so the whole balanced value is
// removed as one unit.
function stripJsxPropsBalanced(code, propNames) {
  const propRe = new RegExp('\\b(?:' + propNames.join('|') + ')=\\{', 'g');
  let result = '';
  let cursor = 0;
  let match;
  while ((match = propRe.exec(code)) !== null) {
    if (match.index < cursor) continue;
    const braceStart = match.index + match[0].length - 1;
    let depth = 0;
    let end = braceStart;
    for (; end < code.length; end++) {
      const ch = code[end];
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) { end++; break; }
      }
    }
    result += code.slice(cursor, match.index);
    cursor = end;
    propRe.lastIndex = end;
  }
  result += code.slice(cursor);
  return result;
}

/**
 * Attempt to fix common JSX syntax issues that crash Babel.
 * Returns fixed code or null if unfixable.
 */
function fixColonAttrs(text) {
  // Fix boolean attrs: required: '' or required: "" → required
  text = text.replace(/(\s)(\w+):\s*(['"])\3/g, '$1$2');
  // Fix attr: "value" → attr="value"
  text = text.replace(/(\s)(\w+):\s*"([^"]*)"/g, '$1$2="$3"');
  // Fix attr: 'value' → attr="value"
  text = text.replace(/(\s)(\w+):\s*'([^']+)'/g, '$1$2="$3"');
  // Fix attr: {expr} → attr={expr}
  text = text.replace(/(\s)(\w+):\s*(\{[^}]+\})/g, '$1$2=$3');
  return text;
}
function tryFixJsxSyntax(code) {
  let fixed = code;
  // UNSCOPED-CORRUPTION-FIX: this used to also run three whole-file,
  // line-based regexes here ("standalone colon attribute lines") matching
  // ANY `word: '...'`/`word: "..."`/`word: ''` line anywhere in the file,
  // not just inside JSX tags — confirmed live, it rewrote an unrelated
  // plain-object property (`address: '123 Main St'` in a data array) into
  // invalid `address="123 Main St"`, crashing the whole file. Removed —
  // the tag-scoped fix directly below (matched against actual `<Tag ...>`
  // text via regex, not arbitrary file-wide lines) already catches
  // genuine colon-attr mistakes inside real JSX without this collateral
  // damage.
  // Fix JSX colon attributes inside tags
  fixed = fixed.replace(/<[a-zA-Z][\w.]*[^>]*\/?>/g, (tag) => {
    if (tag.includes('{{')) {
      const parts = tag.split(/(\{\{[^}]*\}\})/);
      return parts.map(p => p.startsWith('{{') ? p : fixColonAttrs(p)).join('');
    }
    return fixColonAttrs(tag);
  });
  // Fix double closing tags
  fixed = fixed.replace(/(<\/\w+>)>/g, '$1');
  // Fix empty element attributes
  fixed = fixed.replace(/\s+element=\{\s*\}/g, '');
  // Fix empty JSX expressions in attributes
  fixed = fixed.replace(/(\w+)=\{\s*\}(?=\s*[\/>])/g, '');
  // Fix unbalanced JSX: count open/close for major components
  const majorTags = ['BrowserRouter', 'Router', 'Routes', 'Layout', 'div'];
  for (const tag of majorTags) {
    const openCount = (fixed.match(new RegExp('<' + tag + '[\\s>]', 'g')) || []).length;
    const closeCount = (fixed.match(new RegExp('</' + tag + '>', 'g')) || []).length;
    // More closes than opens — remove extra closing tags
    if (closeCount > openCount) {
      for (let i = 0; i < closeCount - openCount; i++) {
        const lastIdx = fixed.lastIndexOf('</' + tag + '>');
        if (lastIdx > -1) {
          fixed = fixed.substring(0, lastIdx) + fixed.substring(lastIdx + tag.length + 3);
        }
      }
    }
    // More opens than closes — add closing tags before );
    if (openCount > closeCount) {
      const returnClose = fixed.lastIndexOf(');');
      if (returnClose > -1) {
        let closings = '';
        for (let i = 0; i < openCount - closeCount; i++) {
          closings += '\n    </' + tag + '>';
        }
        fixed = fixed.substring(0, returnClose) + closings + '\n  ' + fixed.substring(returnClose);
      }
    }
  }
  return fixed;
}

const DEFAULT_CSS = `* { margin:0; padding:0; box-sizing:border-box; }
body { font-family:-apple-system,BlinkMacSystemFont,"Segoe UI","Inter",sans-serif; background:#f9fafb; color:#1e293b; line-height:1.6; }
.flex { display:flex; }
.h-screen { height:100vh; }
.w-64 { width:16rem; }
.bg-white { background:#fff; }
.shadow-lg { box-shadow:0 10px 15px -3px rgba(0,0,0,.1); }
.p-4 { padding:1rem; }
.p-6 { padding:1.5rem; }
.p-8 { padding:2rem; }
.m-4 { margin:1rem; }
.mt-4 { margin-top:1rem; }
.mb-4 { margin-bottom:1rem; }
.text-xl { font-size:1.25rem; }
.text-2xl { font-size:1.5rem; }
.text-lg { font-size:1.125rem; }
.font-bold { font-weight:700; }
.font-semibold { font-weight:600; }
.rounded { border-radius:.25rem; }
.rounded-lg { border-radius:.5rem; }
.grid { display:grid; }
.grid-cols-2 { grid-template-columns:repeat(2,1fr); }
.grid-cols-3 { grid-template-columns:repeat(3,1fr); }
.gap-4 { gap:1rem; }
.gap-6 { gap:1.5rem; }
.overflow-auto { overflow:auto; }
.flex-1 { flex:1; }
.flex-col { flex-direction:column; }
.items-center { align-items:center; }
.justify-between { justify-content:space-between; }
.border { border:1px solid #e5e7eb; }
.border-b { border-bottom:1px solid #e5e7eb; }
.cursor-pointer { cursor:pointer; }
.hover\\:bg-gray-100:hover { background:#f3f4f6; }
h1 { font-size:36px; font-weight:800; color:#0f172a; margin-bottom:16px; }
h2 { font-size:28px; font-weight:700; color:#1e293b; margin-bottom:12px; }
h3 { font-size:20px; font-weight:600; color:#334155; margin-bottom:8px; }
p { line-height:1.8; color:#475569; margin-bottom:12px; }
a { color:#6366f1; text-decoration:none; }
button,[type="submit"] { padding:12px 28px; border:none; border-radius:10px; background:linear-gradient(135deg,#6366f1,#8b5cf6); color:white; font-weight:600; cursor:pointer; font-size:15px; transition:all 0.2s; box-shadow:0 4px 14px rgba(99,102,241,0.25); font-family:inherit; }
input,textarea,select { padding:12px 16px; border:2px solid #e2e8f0; border-radius:10px; font-size:14px; width:100%; outline:none; transition:all 0.2s; font-family:inherit; }
label { display:block; font-weight:600; margin-bottom:8px; font-size:13px; color:#475569; }
ul { list-style:none; }
li { padding:12px 16px; border-bottom:1px solid #f1f5f9; }
header,nav { background:linear-gradient(135deg,#0f172a,#1e293b); color:white; padding:18px 36px; display:flex; align-items:center; justify-content:space-between; }
footer { background:#0f172a; color:#94a3b8; padding:40px 36px; text-align:center; margin-top:60px; }
section { padding:60px 24px; }
.container { max-width:1100px; margin:0 auto; padding:32px 24px; }
img { max-width:100%; border-radius:12px; }
`;

// GENERIC-FIX-5 — Updated styles with toolbar and badge
const styles = {
  container: {
    display: 'flex', flexDirection: 'column',
    height: '100%', overflow: 'hidden',
    background: 'var(--bg-primary)',
    borderRadius: 0,
    position: 'relative',
  },
  empty: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    height: '100%', gap: 10,
    background: 'var(--bg-card)',
    color: 'var(--text-muted)',
  },
  toolbar: {
    display: 'flex', flexDirection: 'column',
    background: 'var(--bg-card)',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
    boxShadow: '0 1px 0 rgba(99,102,241,0.05)',
  },
  toolbarTop: {
    display: 'flex', alignItems: 'center',
    gap: 8, padding: '6px 12px',
  },
  badge: {
    fontSize: 10, fontWeight: 700,
    color: 'var(--text-muted)',
    letterSpacing: '0.06em', textTransform: 'uppercase',
    background: 'rgba(99,102,241,0.07)',
    border: '1px solid rgba(99,102,241,0.14)',
    padding: '2px 8px', borderRadius: 5,
  },
  previewWrapper: {
    display: 'flex',
    flex: 1,
    background: 'var(--bg-secondary)',
    padding: '8px',
    minHeight: 0,
    overflow: 'auto',
  },
  deviceFrame: {
    boxShadow: '0 8px 32px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.08)',
    overflow: 'hidden', flexShrink: 0,
    transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
    borderRadius: 12,
  },
  previewSkeletonOverlay: {
    position: 'absolute', inset: 0, zIndex: 5,
    background: 'var(--bg-secondary)',
    pointerEvents: 'none',
  },
};
